import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { createPortal } from "react-dom";
import "./components/Btn.css";
import HeroAurora from "./components/HeroAurora";
import WhyRail from "./components/WhyRail";
import { PdfResultPreview } from "./PdfBlobPreview";
import PdfEditor from "./editor/PdfEditor";
import { fileIndexForResult } from "./editor/bboxUtils";
import PrivacyPolicyPage from "./PrivacyPolicyPage";
import { StripeTrustLine } from "./components/DataTrustSection";
import ProcessSteps from "./components/ProcessSteps";
import ProcessingCards from "./components/ProcessingCards";
import Rotellina from "./components/Rotellina";
import { pollJobPreviewUntilDone as pollJob } from "./jobPolling";
import { inParallelo } from "./parallelo";
import { creaZip, nomeUnico } from "./zipWriter";
import { C, F, type, btnSizes, btnBase } from "./theme/tokens";
import { AccountProvider, useAccount } from "./account/useAccount";
import { paginaDaUrl, parametri, urlPerPagina } from "./navigazione";
import { aggiornaSessioneLavoro, chiaveLavori, chiediRiapertura, dimenticaDallaCronologia, dimenticaLavoro, leggiLavoro, oraScadenza, prendiRiapertura, salvaLavoro, voceDaLinkEmail } from "./recupero";
import { applicaRedazioni } from "./editor/redazioneBatch";
import { LISTINO_STATICO, NOTA_LISTINO, pianiAbbonabili, prezzoPerCadenza } from "./pricing/listino";
import { chiediReportConformita } from "./reportConformita";
import { BottonePiano, PIANO_IN_EVIDENZA, SchedaPiano, SelettoreCadenza, TINTE, azionePiano } from "./pricing/SchedaPiano";
import { generaMiniatura, revocaMiniatura, tipoFile } from "./miniature";
import AccessoPage from "./account/AccessoPage";
import RegistrazionePage, { ricordaPianoScelto } from "./account/RegistrazionePage";
import VerificaPage from "./account/VerificaPage";
import PasswordPage from "./account/PasswordPage";
import InvitoPage from "./account/InvitoPage";
import AccountPage from "./account/AccountPage";
import Sidebar from "./account/Sidebar";
import { UsaParoleSalvate } from "./account/ParoleSalvate";
import { controllaLimiti, LIMITI_PREDEFINITI, limitiValidi, messaggioScartati, separaTroppoGrandi, testoLimiti } from "./limitiCaricamento";
import { caricaSuS3 } from "./caricamentoDiretto";
import { motivoCheckout, PAGAMENTO_NON_AVVIATO } from "./erroriPagamento";
import { ALTEZZA_BARRA, BottoneEsci, BottoneMenu } from "./account/ui";
import CookiePolicyPage from "./CookiePolicyPage";
import CookieBanner from "./consenso/CookieBanner";
import { apriPreferenzeCookie } from "./consenso/consenso";
import { traccia } from "./consenso/tracciamento";

/* ═══════════════════════════════════════════
   DESIGN SYSTEM (i colori C vivono in theme/tokens.js)
   ═══════════════════════════════════════════ */
/** Navbar GRID+ (theme ideology.digital) */
const NAV_TOP_GAP = 15;
const NAV_GRID = {
  bg: "rgba(231, 246, 255, 0.96)",
  link: "#032b51",
  linkHover: "#009cdd",
  btn: "#0a3458",
  radius: 10,
  shadow: "rgba(24, 46, 79, 0.05) 0px 6px 24px 0px, rgba(24, 46, 79, 0.04) 0px 0px 0px 1px",
};
const ease = "cubic-bezier(.25,1,.5,1)";
// ACCEDI nella top bar: la forma del pulsante principale, ma celeste, con
// il gradiente degli altri pulsanti e il testo bianco.
const NAV_ACCOUNT_BG = `linear-gradient(90deg, ${C.smartBlue} 0%, ${C.blueAccent} 100%)`;
const NAV_ACCOUNT_HOVER = `linear-gradient(90deg, ${C.blueAccent} 0%, ${C.smartBlue} 100%)`;
const OBSCURA_LOGO = `${process.env.PUBLIC_URL || ""}/obscura-logo.png`;
const SAPIENZA_LOGO_WHITE = `${process.env.PUBLIC_URL || ""}/logo_Sapienza_bianco.png`;
const SAPIENZA_LOGO_DARK = `${process.env.PUBLIC_URL || ""}/logo_Sapienza_nero.png`;
const GRID_PLUS_LOGO_WHITE = `${process.env.PUBLIC_URL || ""}/logo_gridplus_bianco.png`;
const GRID_PLUS_LOGO = `${process.env.PUBLIC_URL || ""}/logo_gridplus.png`;
const PRIVACY_POLICY_URL = "?page=privacy";
function useViewport() {
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 1200 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return { width, isMobile: width < 768, isTablet: width < 1024 };
}
function useMagnet(ref, strength = 6) {
  const [t, setT] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / r.width;
      const dy = (e.clientY - cy) / r.height;
      setT({ x: dx * strength, y: dy * strength });
    };
    const leave = () => setT({ x: 0, y: 0 });
    el.addEventListener("mousemove", move);
    el.addEventListener("mouseleave", leave);
    return () => {
      el.removeEventListener("mousemove", move);
      el.removeEventListener("mouseleave", leave);
    };
  }, [strength]);
  return t;
}
function getWrapStyle(width, maxWidth = 1200) {
  const pad = width < 480 ? 16 : width < 768 ? 20 : width < 1200 ? 28 : 40;
  return { maxWidth, margin: "0 auto", padding: `0 ${pad}px` };
}

/* ─── Shared ─── */
function Reveal({ children, delay = 0, style: s = {} }) {
  const ref = useRef(null); const [v, setV] = useState(false);
  useEffect(() => { const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setTimeout(() => setV(true), delay); obs.disconnect(); } }, { threshold: 0.1 }); if (ref.current) obs.observe(ref.current); return () => obs.disconnect(); }, [delay]);
  return <div ref={ref} style={{ ...s, opacity: v ? 1 : 0, transform: v ? "translateY(0)" : "translateY(24px)", transition: `opacity 0.7s ${ease} ${delay}ms, transform 0.7s ${ease} ${delay}ms` }}>{children}</div>;
}
const Ico = ({ children, size = 30, color = C.smartBlue }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{children}</svg>;
const icons = {
  shield: <Ico><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></Ico>,
  doc: <Ico><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></Ico>,
  globe: <Ico><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></Ico>,
  lock: <Ico><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Ico>,
  coins: <Ico><circle cx="12" cy="12" r="10"/><path d="M12 6v12"/><path d="M8 10h8"/><path d="M8 14h8"/></Ico>,
  users: <Ico><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Ico>,
  chat: <Ico><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></Ico>,
  check: <Ico size={18} color={C.smartBlue}><polyline points="20 6 9 17 4 12"/></Ico>,
  alert: <Ico size={14} color={C.textNum}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></Ico>,
  arrow: <Ico size={12} color="#fff"><path d="M5 12h14M12 5l7 7-7 7"/></Ico>,
  upload: <Ico size={22} color="currentColor"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></Ico>,
  // Nuvoletta con freccia: dice "carica" senza bisogno di leggere. Sostituisce
  // lo scudo in cima all'area di caricamento, che parlava di sicurezza in un
  // punto in cui all'utente serve sapere dove mettere i file.
  uploadCloud: <Ico size={40} color={C.smartBlue}><path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97 6 6 0 0 0-11.6-1.6A4 4 0 0 0 6.5 19"/><polyline points="9 13 12 10 15 13"/><line x1="12" y1="10" x2="12" y2="20"/></Ico>,
  trash: <Ico size={16} color={C.textNum}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></Ico>,
  chevron: <Ico size={16} color={C.textNum}><polyline points="6 9 12 15 18 9"/></Ico>,
  reset: <Ico size={16} color={C.textNum}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></Ico>,
  shieldStar: <Ico size={56} color={C.smartBlue}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8l1.12 3.45h3.63l-2.93 2.13 1.12 3.45L12 14.9l-2.94 2.13 1.12-3.45-2.93-2.13h3.63z" fill={C.smartBlue} stroke="none"/></Ico>,
  grid: <Ico size={18} color={C.textNum}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></Ico>,
  file: <Ico size={40} color={C.smartBlue}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></Ico>,
  star: <Ico size={18} color="#f59e0b"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" fill="#f59e0b"/></Ico>,
  building: <Ico><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></Ico>,
  server: <Ico><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></Ico>,
};

const GBTN_SIZES = btnSizes;

const ArrowRight = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

// Gli sfondi a gradiente dell'hover. Il CSS non sa sfumare un colore pieno in
// un gradiente: all'uscita il gradiente spariva di colpo e per mezzo secondo
// il pulsante restava quasi trasparente, con il testo grigio. Ora il gradiente
// sta in uno strato sopra lo sfondo, che compare e scompare con l'opacita':
// fluido nei due versi.
const SFONDI_HOVER = {
  primaryLight: "linear-gradient(90deg, #B7DAE9 0%, #EAF8FF 48%, #ffffff 100%)",
  ghostLight: "linear-gradient(90deg, rgba(89,201,244,0.62) 0%, rgba(89,201,244,0.2) 34%, rgba(6,20,39,0.55) 68%, rgba(6,20,39,0.38) 100%)",
  primary: `linear-gradient(90deg, ${C.smartBlue} 0%, ${C.blueAccent} 100%)`,
  pill: `linear-gradient(90deg, ${C.smartBlue}, ${C.blueAccent})`,
  hero: `linear-gradient(90deg, ${C.smartBlue} 0%, ${C.blueAccent} 100%)`,
};

// Il focus accende il pulsante solo se arriva dalla tastiera: dopo un clic il
// pulsante resta a fuoco, e prima restava acceso finche' non si cliccava altrove.
function focusDaTastiera(e) {
  try {
    return e.currentTarget.matches(":focus-visible");
  } catch (_) {
    return true; // browser senza :focus-visible: come prima
  }
}

// `bordo`: lo spessore del bordo del pulsante. Lo strato lo copre anche, se
// no sotto il bordo trasparente resta un filo del colore di riposo.
function StratoHover({ sfondo, acceso, bordo = 0 }) {
  return (
    <span
      aria-hidden="true"
      data-ob-strato
      style={{
        position: "absolute",
        inset: -bordo,
        borderRadius: "inherit",
        background: sfondo,
        opacity: acceso ? 1 : 0,
        transition: `opacity 0.35s ${ease}`,
        pointerEvents: "none",
      }}
    />
  );
}

function GBtn({ children, variant = "primary", size = "md", style: s = {}, onClick, href, iconRight, disableMagnet = false }) {
  const [h, setH] = useState(false);
  const ref = useRef(null);
  const m = useMagnet(ref, disableMagnet ? 0 : 6);
  const sz = GBTN_SIZES[size] || GBTN_SIZES.md;

  const base = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    ...sz,
    borderRadius: btnBase.borderRadius,
    fontWeight: btnBase.fontWeight,
    fontFamily: btnBase.fontFamily,
    fontSize: sz.fontSize,
    textTransform: "uppercase",
    letterSpacing: btnBase.letterSpacing,
    textDecoration: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: `background 0.45s ${ease}, color 0.3s ${ease}, border-color 0.3s ${ease}, box-shadow 0.35s ${ease}, transform 0.35s ${ease}`,
    border: "none",
    position: "relative",
    transform: disableMagnet ? "none" : `translate3d(${m.x}px, ${m.y}px, 0)`,
  };

  // Nelle varianti con un gradiente in SFONDI_HOVER lo sfondo resta quello di
  // riposo anche in hover: il gradiente lo porta lo strato (StratoHover).
  const v = {
    primaryLight: h
      ? {
          background: C.smartBlue,
          color: C.deepText,
          boxShadow: "0 14px 42px rgba(255,255,255,0.28), 0 4px 18px rgba(89,201,244,0.18)",
        }
      : {
          background: C.smartBlue,
          color: "#fff",
          boxShadow: "0 8px 28px rgba(0,156,221,0.32)",
        },
    ghostLight: h
      ? {
          background: "rgba(255,255,255,0.04)",
          color: "#fff",
          border: `1px solid rgba(255,255,255,0.42)`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 10px 32px rgba(89,201,244,0.14)",
        }
      : {
          background: "rgba(255,255,255,0.04)",
          color: "#fff",
          border: `1px solid rgba(255,255,255,0.18)`,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        },
    primary: h
      ? {
          background: C.btnBg,
          color: "#fff",
          boxShadow: "0 14px 40px rgba(89,201,244,0.4)",
        }
      : {
          background: C.btnBg,
          color: "#fff",
          boxShadow: "0 8px 24px rgba(0,156,221,0.28)",
        },
    white: h
      ? { background: C.smartBlue20, color: C.dataNight, boxShadow: C.shadowMd }
      : { background: "#fff", color: C.dataNight, boxShadow: C.shadow },
    pill: {
      background: C.dataNight,
      color: "#fff",
      borderRadius: 50,
      fontSize: 14,
      fontWeight: 500,
    },
    outline: h
      ? { color: C.smartBlue, border: `1.5px solid ${C.smartBlue}`, background: C.smartBlue10 }
      : { background: "transparent", color: C.textNum, border: `1.5px solid ${C.border}` },
    blue: h
      ? { background: "#0088c4", color: "#fff", borderRadius: 999 }
      : { background: C.smartBlue, color: "#fff", borderRadius: 999 },
    blueOutline: h
      ? { background: C.smartBlue, color: "#fff", borderRadius: 999, border: `1.5px solid ${C.smartBlue}` }
      : { background: "transparent", color: C.smartBlue, border: `1.5px solid ${C.smartBlue}`, borderRadius: 999 },
    hero: h
      ? {
          background: C.smartBlue,
          color: "#fff",
          borderRadius: 14,
          padding: "0 48px",
          height: 64,
          fontSize: 18,
          fontWeight: 600,
          letterSpacing: "0.3px",
          textTransform: "none",
          boxShadow: "0 14px 40px rgba(89,201,244,0.4)",
        }
      : {
          background: C.smartBlue,
          color: "#fff",
          borderRadius: 14,
          padding: "0 48px",
          height: 64,
          fontSize: 18,
          fontWeight: 600,
          letterSpacing: "0.3px",
          textTransform: "none",
          boxShadow: "0 6px 24px rgba(0,156,221,0.15)",
        },
  };

  const showArrowBox = variant === "primary" && iconRight !== false;
  const showArrowSvg = (variant === "primaryLight" || variant === "hero") && iconRight !== false;
  const setHover = (on) => () => setH(on);
  // Chi impone il proprio sfondo (la top bar) non vuole il gradiente sopra.
  const sfondoHover = s.background === undefined ? SFONDI_HOVER[variant] : null;
  const Tag = href ? "a" : "button";
  const tagProps = href
    ? { href, onClick, ref }
    : { type: "button", onClick, ref };

  return (
    <Tag
      {...tagProps}
      data-ob-btn
      data-ob-variant={variant}
      onMouseEnter={setHover(true)}
      onMouseLeave={setHover(false)}
      onFocus={(e) => { if (focusDaTastiera(e)) setH(true); }}
      onBlur={setHover(false)}
      style={{ ...base, ...v[variant], ...s }}
    >
      {sfondoHover && <StratoHover sfondo={sfondoHover} acceso={h} />}
      <span style={{ position: "relative", zIndex: 1, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>{children}</span>
      {showArrowBox && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: 6,
            // Il riquadro della freccia resta celeste anche in hover: si
            // sposta e basta. Diventava blu notte, quasi invisibile sul
            // pulsante scuro della top bar.
            background: C.blueAccent,
            transition: `transform 0.4s ${ease}`,
            marginLeft: 4,
            position: "relative",
            zIndex: 1,
            transform: `translateX(${h ? 4 : 0}px)`,
          }}
        >
          {icons.arrow}
        </span>
      )}
      {showArrowSvg && (
        <span
          data-ob-arrow
          style={{
            display: "inline-flex",
            position: "relative",
            zIndex: 1,
            transform: h ? "translateX(4px)" : "translateX(0)",
            transition: `transform 0.35s ${ease}`,
          }}
        >
          <ArrowRight />
        </span>
      )}
    </Tag>
  );
}
function DecoLines({ variant = 1, style: s = {} }) {
  const p = {
    1: <svg viewBox="0 0 600 800" fill="none" style={{ position: "absolute", ...s, pointerEvents: "none", opacity: 0.18 }}><path d="M350 0C350 200,200 300,100 500C50 600,80 700,100 800" stroke="url(#dl1a)" strokeWidth="18" strokeLinecap="round"/><path d="M420 0C420 180,320 350,350 500C370 600,500 650,520 800" stroke="url(#dl1b)" strokeWidth="14" strokeLinecap="round"/><defs><linearGradient id="dl1a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B7DAE9" stopOpacity=".5"/><stop offset="100%" stopColor="#59C9F4" stopOpacity=".2"/></linearGradient><linearGradient id="dl1b" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#def0f9" stopOpacity=".4"/><stop offset="100%" stopColor="#009CDD" stopOpacity=".15"/></linearGradient></defs></svg>,
    2: <svg viewBox="0 0 500 700" fill="none" style={{ position: "absolute", ...s, pointerEvents: "none", opacity: 0.15 }}><path d="M0 100C100 100,200 200,180 350C160 500,50 550,0 700" stroke="url(#dl2a)" strokeWidth="16" strokeLinecap="round"/><defs><linearGradient id="dl2a" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#B7DAE9" stopOpacity=".4"/><stop offset="100%" stopColor="#59C9F4" stopOpacity=".2"/></linearGradient></defs></svg>,
  };
  return p[variant] || null;
}

const STitle = ({ children, light = false, style: s = {} }) => (
  <h2
    style={{
      ...type.h2,
      color: light ? "#fff" : C.textTitle,
      marginBottom: 12,
      ...s,
    }}
  >
    {children}
  </h2>
);
const SDesc = ({ children, light = false, style: s = {} }) => <p style={{ ...type.body, color: light ? "rgba(255,255,255,0.6)" : C.textNum, maxWidth: 640, ...s }}>{children}</p>;

/* ─── Testimonial Carousel ─── */
function TestimonialCarousel({ isMobile = false }) {
  const [isPaused, setIsPaused] = useState(false);
  const testimonials = [
    { name: "Luca D.", role: "Avvocato", company: "Studio legale associato", photo: "/luca-d.png", text: "Abbiamo potuto testare analisi su fascicoli e contratti senza condividere dati identificativi dei clienti." },
    { name: "Andrea D.", role: "CEO", company: "Azienda FinTech", photo: "/andrea-d.png", text: "La parte utile del documento resta leggibile, ma IBAN, email e codici fiscali spariscono prima di entrare nei workflow AI." },
    { name: "Francesca T.", role: "HR Manager", company: "Azienda manifatturiera", photo: "/francesca.png", text: "Per lo screening dei CV ci serviva un passaggio semplice: anonimizzare, controllare l'output e poi usare l'AI in sicurezza." },
    { name: "Roberto C.", role: "Compliance", company: "Banca privata italiana", photo: "/roberto.png", bw: true, text: "Il modello pay-per-page ci ha permesso di partire senza contratti lunghi, mantenendo un controllo chiaro sui documenti processati." },
    { name: "Alessia M.", role: "DPO", company: "Gruppo assicurativo", photo: "/alessia.png", text: "OBSCURA rende più semplice spiegare internamente dove vengono rimossi i dati personali e cosa resta utilizzabile." },
    { name: "Simona V.", role: "Partner", company: "Studio commercialista", photo: "/simona.png", text: "I documenti fiscali erano il blocco principale all'adozione di strumenti AI. Ora possiamo preparare versioni sicure in pochi passaggi." },
    { name: "Fabrizio C.", role: "CEO", company: "Affitti brevi", photo: "/fabrizio-c.png", text: "Per i team operativi è utile avere un output già pulito, con i dati sensibili rimossi prima dell'analisi." },
  ];

  const items = isMobile ? testimonials : [...testimonials, ...testimonials];
  const getInitials = (name) => name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      style={{ overflow: "hidden", width: "100%", position: "relative" }}
    >
      {!isMobile && <>
        <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 90, zIndex: 2, pointerEvents: "none", background: "linear-gradient(90deg, #fff, rgba(255,255,255,0))" }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: 90, zIndex: 2, pointerEvents: "none", background: "linear-gradient(270deg, #fff, rgba(255,255,255,0))" }} />
      </>}
      <div
        className="testimonial-track"
        style={{
          display: "flex",
          gap: isMobile ? 16 : 24,
          animation: isMobile ? "none" : "testimonial-scroll 120s linear infinite",
          animationPlayState: isPaused ? "paused" : "running",
          width: isMobile ? "100%" : "max-content",
          overflowX: isMobile ? "auto" : "visible",
          scrollSnapType: isMobile ? "x mandatory" : "none",
          padding: isMobile ? "4px 4px 14px" : "8px 0 14px",
          WebkitOverflowScrolling: "touch",
          willChange: isMobile ? "auto" : "transform",
          backfaceVisibility: "hidden",
        }}
      >
        {items.map((t, i) => (
          <div
            key={i}
            style={{
              width: isMobile ? "86vw" : 440,
              maxWidth: isMobile ? 360 : "none",
              minHeight: isMobile ? 240 : 280,
              flexShrink: 0,
              padding: isMobile ? "24px 22px" : "30px 30px",
              borderRadius: 24,
              background: "linear-gradient(180deg, #fff 0%, #fbfeff 100%)",
              border: `1px solid ${C.border}`,
              boxShadow: "0 4px 18px rgba(24,46,79,0.06)",
              display: "flex",
              flexDirection: "column",
              gap: 16,
              position: "relative",
              overflow: "hidden",
              textRendering: "optimizeLegibility",
              scrollSnapAlign: isMobile ? "start" : "none",
            }}
          >
            <span style={{ position: "absolute", right: 28, top: 16, fontSize: 92, lineHeight: 1, color: C.smartBlue10, fontFamily: F.display, fontStyle: "italic", fontWeight: 500, pointerEvents: "none" }}>“</span>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
                <div style={{ width: isMobile ? 56 : 64, height: isMobile ? 56 : 64, borderRadius: "50%", overflow: "hidden", border: `1px solid ${C.smartBlue30}`, background: `linear-gradient(135deg, ${C.smartBlue10}, ${C.smartBlue30})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "0 1px 6px rgba(24,46,79,0.05)" }}>
                  {t.photo ? (
                    <img src={t.photo} alt={t.name} style={{ width: "100%", height: "100%", objectFit: "cover", filter: t.bw ? "grayscale(1) contrast(1.08)" : "none" }} />
                  ) : (
                    <span style={{ fontFamily: F.h, fontSize: isMobile ? 16 : 18, fontWeight: 600, color: C.smartBlue }}>{getInitials(t.name)}</span>
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.textTitle, fontFamily: F.h, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</p>
                  <p style={{ margin: 0, fontSize: 13, color: C.textNum, fontFamily: F.b, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.company}</p>
                </div>
              </div>
            </div>

            <p
              style={{
                fontSize: isMobile ? 16 : 16,
                lineHeight: "1.7",
                color: C.textTitle,
                fontFamily: F.b,
                fontWeight: 400,
                margin: 0,
                flex: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              "{t.text}"
            </p>

            <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", position: "relative", zIndex: 1 }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.8px", textTransform: "uppercase", color: C.smartBlue, background: C.smartBlue10, border: `1px solid ${C.smartBlue20}`, padding: "7px 12px", borderRadius: 999 }}>{t.role}</span>
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes testimonial-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .testimonial-track:hover {
          animation-play-state: paused;
        }
        .testimonial-track::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════
   CATEGORIES (NER codes — keep in sync with API)
   ═══════════════════════════════════════════ */
const CATEGORIES = [
  { name: "Informazioni personali", count: 8, items: ["NOME","COGNOME","DATA_NASCITA","DATA_MORTE","ETA","CODICE_FISCALE","PROFESSIONE","STATO_CIVILE"] },
  { name: "Informazioni di contatto", count: 4, items: ["INDIRIZZO","NUMERO_TELEFONO","EMAIL","CODICE_POSTALE"] },
  { name: "Informazioni finanziarie", count: 11, items: ["VALUTA","IMPORTO","NUMERO_CARTA","CVV","NUMERO_CONTO","IBAN","BIC","P_IVA","TASSO_MUTUO","NUM_ASSEGNO_BANCARIO","BANCA"] },
  { name: "Dati legali", count: 7, items: ["RAGIONE_SOCIALE","TRIBUNALE","LEGGE","N_SENTENZA","N_LICENZA","AVV_NOTAIO","REGIME_PATRIMONIALE"] },
  { name: "Informazioni sanitarie", count: 9, items: ["CARTELLA_CLINICA","MALATTIA","MEDICINA","STORIA_CLINICA","STRENGTH","FREQUENZA","DURATION","DOSAGGIO","FORM"] },
  { name: "Informazioni tecniche", count: 5, items: ["IP","IPV6_1","MAC","USER_AGENT","IMEI"] },
  { name: "Dati geografici e temporali", count: 4, items: ["STATO","LUOGO","ORARIO","DATA"] },
  { name: "Documenti e veicoli", count: 6, items: ["NUMERO_DOCUMENTO","TARGA_VEICOLO","FOGLIO","PARTICELLA","MAPPALE","SUBALTERNO"] },
  { name: "Web e sicurezza", count: 4, items: ["URL","PASSWORD","PIN","BRAND"] },
];

/** Display labels for NER codes (Italian). Keys mirror `CATEGORIES`. */
const PII_LABELS_IT = {
  NOME: "Nome", COGNOME: "Cognome", DATA_NASCITA: "Data di nascita", DATA_MORTE: "Data di morte", ETA: "Età",
  CODICE_FISCALE: "Codice fiscale", PROFESSIONE: "Professione", STATO_CIVILE: "Stato civile",
  INDIRIZZO: "Indirizzo", NUMERO_TELEFONO: "Telefono", EMAIL: "Email", CODICE_POSTALE: "CAP",
  VALUTA: "Valuta", IMPORTO: "Importo", NUMERO_CARTA: "Carta di pagamento", CVV: "CVV", NUMERO_CONTO: "Numero conto",
  IBAN: "IBAN", BIC: "BIC", P_IVA: "Partita IVA", TASSO_MUTUO: "Tasso mutuo", NUM_ASSEGNO_BANCARIO: "Numero assegno", BANCA: "Banca",
  RAGIONE_SOCIALE: "Ragione sociale", TRIBUNALE: "Tribunale", LEGGE: "Riferimento normativo", N_SENTENZA: "Numero sentenza",
  N_LICENZA: "Numero licenza", AVV_NOTAIO: "Avvocato / Notaio", REGIME_PATRIMONIALE: "Regime patrimoniale",
  CARTELLA_CLINICA: "Cartella clinica", MALATTIA: "Patologia", MEDICINA: "Terapia farmacologica", STORIA_CLINICA: "Storia clinica",
  STRENGTH: "Dosaggio", FREQUENZA: "Frequenza", DURATION: "Durata", DOSAGGIO: "Posologia", FORM: "Forma farmaceutica",
  IP: "Indirizzo IP", IPV6_1: "IPv6", MAC: "MAC address", USER_AGENT: "User agent", IMEI: "IMEI",
  STATO: "Stato", LUOGO: "Luogo", ORARIO: "Orario", DATA: "Data",
  NUMERO_DOCUMENTO: "Numero documento", TARGA_VEICOLO: "Targa", FOGLIO: "Foglio catastale", PARTICELLA: "Particella",
  MAPPALE: "Mappale", SUBALTERNO: "Subalterno",
  URL: "URL", PASSWORD: "Password", PIN: "PIN", BRAND: "Marchio",
};

const PII_ICON_SM = 14;
const PII_GROUP_ICONS = {
  "Informazioni personali": <Ico size={PII_ICON_SM}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Ico>,
  "Informazioni di contatto": <Ico size={PII_ICON_SM}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></Ico>,
  "Informazioni finanziarie": <Ico size={PII_ICON_SM}><circle cx="12" cy="12" r="10"/><path d="M12 6v12"/><path d="M8 10h8"/><path d="M8 14h8"/></Ico>,
  "Dati legali": <Ico size={PII_ICON_SM}><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4"/><path d="M10 10h4"/><path d="M10 14h4"/><path d="M10 18h4"/></Ico>,
  "Informazioni sanitarie": <Ico size={PII_ICON_SM}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></Ico>,
  "Informazioni tecniche": <Ico size={PII_ICON_SM}><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></Ico>,
  "Dati geografici e temporali": <Ico size={PII_ICON_SM}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z"/></Ico>,
  "Documenti e veicoli": <Ico size={PII_ICON_SM}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></Ico>,
  "Web e sicurezza": <Ico size={PII_ICON_SM}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Ico>,
};

/** 58 voci da CATEGORIES, interleave round-robin tra i 9 macro-gruppi. */
function buildInterleavedPiiItems() {
  const groups = CATEGORIES.map((cat) =>
    cat.items.map((code) => ({
      code,
      label: PII_LABELS_IT[code],
      icon: PII_GROUP_ICONS[cat.name],
    }))
  );
  const maxLen = Math.max(...groups.map((g) => g.length));
  const out = [];
  for (let i = 0; i < maxLen; i += 1) {
    groups.forEach((g) => { if (g[i]) out.push(g[i]); });
  }
  return out;
}
const PII_MARQUEE_ITEMS = buildInterleavedPiiItems();

const SLIDER_AR_W = 400;
const SLIDER_AR_H = 560;

/** Before/after sul solo rendering PDF: trascina sulla pagina; tastiera ← → quando il riquadro ha il focus.
 *  Con `fitSize` (desktop a due colonne) il riquadro resta in proporzione 400:560 ma non supera l'altezza indicata.
 *
 *  Non c'e' piu' una versione "senza animazioni" a due riquadri affiancati:
 *  scattava con prefers-reduced-motion, che Windows attiva quando gli
 *  effetti di animazione sono spenti o in risparmio energetico, e il
 *  confronto compariva sdoppiato. Lo slider si muove solo se lo trascini,
 *  quindi non e' un'animazione e non ha motivo di sparire. */
function BeforeAfterPdfSlider({ beforeSrc, afterSrc, isMobile, fitSize }) {
  const wrapRef = useRef(null);
  const overlayRef = useRef(null);
  const shellRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragRef = useRef(false);
  const base = process.env.PUBLIC_URL || "";
  const beforeUrl = `${base}${beforeSrc}`;
  const afterUrl = `${base}${afterSrc}`;
  const imgStyle = { width: "100%", height: "100%", objectFit: "contain", display: "block" };
  const fitted = fitSize && fitSize.width > 0 && fitSize.height > 0;

  const updateFromClientX = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const p = Math.round(((clientX - rect.left) / rect.width) * 100);
    setPos(Math.min(100, Math.max(0, p)));
  }, []);

  useEffect(() => {
    const stop = () => { dragRef.current = false; };
    const move = (e) => {
      if (!dragRef.current) return;
      updateFromClientX(e.clientX);
    };
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    window.addEventListener("pointermove", move);
    return () => {
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      window.removeEventListener("pointermove", move);
    };
  }, [updateFromClientX]);

  const startDrag = (e) => {
    dragRef.current = true;
    updateFromClientX(e.clientX);
    shellRef.current?.focus({ preventScroll: true });
    try {
      overlayRef.current?.setPointerCapture(e.pointerId);
    } catch (_) { /* noop */ }
  };

  const endDrag = (e) => {
    dragRef.current = false;
    try {
      overlayRef.current?.releasePointerCapture(e.pointerId);
    } catch (_) { /* noop */ }
  };

  return (
    <div
      ref={shellRef}
      role="slider"
      aria-valuenow={pos}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={`Linea di confronto al ${pos} per cento dalla sinistra`}
      aria-label="Confronto prima e dopo anonimizzazione. Trascina orizzontalmente sulla pagina oppure usa le frecce sulla tastiera."
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          setPos((p) => Math.max(0, p - 4));
        }
        if (e.key === "ArrowRight") {
          e.preventDefault();
          setPos((p) => Math.min(100, p + 4));
        }
      }}
      style={{
        position: "relative",
        width: fitted ? fitSize.width : "100%",
        height: fitted ? fitSize.height : undefined,
        margin: fitted ? "0 auto" : undefined,
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid ${C.border}`,
        outline: "none",
        boxShadow: C.shadow,
        boxSizing: "border-box",
      }}
    >
      <div
        ref={wrapRef}
        style={{
          position: "relative",
          width: "100%",
          height: fitted ? "100%" : undefined,
          aspectRatio: fitted ? undefined : `${SLIDER_AR_W} / ${SLIDER_AR_H}`,
          background: "#e8eef4",
          touchAction: "none",
        }}
      >
        <img src={afterUrl} alt="" style={{ ...imgStyle, position: "absolute", inset: 0 }} loading="lazy" decoding="async" />
        <div style={{ position: "absolute", inset: 0, clipPath: `inset(0 ${100 - pos}% 0 0)`, WebkitClipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img src={beforeUrl} alt="" style={imgStyle} loading="lazy" decoding="async" />
        </div>
        <span
          aria-hidden
          style={{
            position: "absolute",
            bottom: 10,
            left: 10,
            padding: "5px 11px",
            borderRadius: 8,
            fontSize: 10,
            fontWeight: 800,
            fontFamily: F.b,
            letterSpacing: "0.12em",
            pointerEvents: "none",
            zIndex: 3,
            background: "rgba(24,46,79,0.82)",
            color: "#fff",
            boxShadow: "0 2px 10px rgba(24,46,79,0.12)",
          }}
        >
          PRIMA
        </span>
        <span
          aria-hidden
          style={{
            position: "absolute",
            bottom: 10,
            right: 10,
            padding: "5px 11px",
            borderRadius: 8,
            fontSize: 10,
            fontWeight: 800,
            fontFamily: F.b,
            letterSpacing: "0.12em",
            pointerEvents: "none",
            zIndex: 3,
            background: "rgba(0,156,221,0.88)",
            color: "#fff",
            boxShadow: "0 2px 10px rgba(24,46,79,0.12)",
          }}
        >
          DOPO
        </span>
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${pos}%`,
            width: 3,
            marginLeft: -1.5,
            background: C.smartBlue,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.95)",
            pointerEvents: "none",
            zIndex: 2,
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: "50%",
            left: `${pos}%`,
            transform: "translate(-50%, -50%)",
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "#fff",
            border: `3px solid ${C.smartBlue}`,
            pointerEvents: "none",
            zIndex: 3,
            boxShadow: "0 4px 14px rgba(24,46,79,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 800, color: C.smartBlue, lineHeight: 1, fontFamily: F.h }}>‹</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: C.smartBlue, lineHeight: 1, fontFamily: F.h }}>›</span>
        </div>
        <div
          ref={overlayRef}
          onPointerDown={startDrag}
          onPointerUp={endDrag}
          style={{ position: "absolute", inset: 0, zIndex: 4, cursor: "ew-resize" }}
        />
      </div>
    </div>
  );
}

/* ─── Tab Section (5 sectors) ─── */
function TabSection({ onNavigate, isMobile = false, isTablet = false }) {
  const [a, setA] = useState(0);
  const leftColRef = useRef(null);
  const rightColRef = useRef(null);
  const [sliderFit, setSliderFit] = useState(null);
  const tabs = [
    {
      tag: "Legale",
      pts: ["Anonimizza atti, contratti, sentenze prima di analizzarli con AI", "Proteggi i dati dei clienti in documenti legali sensibili", "Condividi fascicoli con consulenti esterni senza rischi GDPR"],
      cta: "Prova OBSCURA", beforeImage: "/samples/Accordo_riservatezza_NDA.png", afterImage: "/samples/Accordo_riservatezza_NDA_redacted.png",
      piiKeys: ["NOME", "COGNOME", "DATA_NASCITA", "CODICE_FISCALE", "INDIRIZZO", "EMAIL", "NUMERO_TELEFONO", "IBAN", "RAGIONE_SOCIALE", "TRIBUNALE", "N_SENTENZA", "LEGGE", "AVV_NOTAIO", "P_IVA", "IMPORTO", "REGIME_PATRIMONIALE"],
    },
    {
      tag: "HR",
      pts: ["Analizza CV e lettere di presentazione con AI in sicurezza", "Proteggi dati personali dei candidati e dipendenti", "Automatizza lo screening documentale compliant col GDPR"],
      cta: "Prova OBSCURA", beforeImage: "/samples/CV_esempio.png", afterImage: "/samples/CV_esempio_redacted.png",
      piiKeys: ["NOME", "COGNOME", "DATA_NASCITA", "LUOGO", "INDIRIZZO", "EMAIL", "NUMERO_TELEFONO", "CODICE_POSTALE", "CODICE_FISCALE", "PROFESSIONE", "STATO_CIVILE", "RAGIONE_SOCIALE", "ETA", "NUMERO_DOCUMENTO", "URL", "BRAND"],
    },
    {
      tag: "Sanità",
      pts: ["Anonimizza cartelle cliniche e referti per ricerca o analisi AI", "Proteggi dati dei pazienti in conformità col GDPR", "Condividi documentazione clinica in sicurezza"],
      cta: "Prova OBSCURA", beforeImage: "/samples/medicina.png", afterImage: "/samples/medicina_redacted.png",
      piiKeys: ["NOME", "COGNOME", "DATA_NASCITA", "LUOGO", "INDIRIZZO", "NUMERO_DOCUMENTO", "CARTELLA_CLINICA", "MALATTIA", "MEDICINA", "STORIA_CLINICA", "DOSAGGIO", "DATA", "ORARIO", "FREQUENZA", "FORM", "STRENGTH"],
    },
    {
      tag: "Amministrazione",
      pts: ["Anonimizza documenti catastali, fiscali, anagrafici", "Usa AI per analizzare pratiche senza rischi di data breach", "Gestisci volumi di documenti con anonimizzazione automatica"],
      cta: "Prova OBSCURA", beforeImage: "/samples/visura_catastale.png", afterImage: "/samples/visura_catastale_redacted.png",
      piiKeys: ["NOME", "COGNOME", "CODICE_FISCALE", "LUOGO", "INDIRIZZO", "CODICE_POSTALE", "IMPORTO", "P_IVA", "NUMERO_DOCUMENTO", "FOGLIO", "PARTICELLA", "SUBALTERNO", "MAPPALE", "TARGA_VEICOLO", "IBAN", "BIC"],
    },
    {
      tag: "AI & Data",
      pts: ["Prepara dataset documentali per training/fine-tuning sicuro", "Integra via API nel tuo pipeline di machine learning", "Garantisci compliance privacy su tutti i dati in input ai modelli"],
      cta: "Prova OBSCURA", beforeImage: "/samples/nota_IT.png", afterImage: "/samples/nota_IT_redacted.png",
      piiKeys: ["NOME", "COGNOME", "EMAIL", "CODICE_FISCALE", "LUOGO", "INDIRIZZO", "NUMERO_TELEFONO", "IP", "IPV6_1", "MAC", "USER_AGENT", "URL", "PASSWORD", "IMEI", "PIN", "BRAND"],
    },
  ];
  const t = tabs[a];
  const tabId = "sector-tab";
  const panelId = "sector-panel";

  useLayoutEffect(() => {
    if (isMobile || isTablet) {
      setSliderFit(null);
      return undefined;
    }
    const measure = () => {
      const left = leftColRef.current;
      const right = rightColRef.current;
      if (!left || !right) return;
      const lh = left.offsetHeight;
      const rw = right.clientWidth;
      if (lh <= 0 || rw <= 0) return;
      const w = Math.min(rw, (lh * SLIDER_AR_W) / SLIDER_AR_H);
      const h = (w * SLIDER_AR_H) / SLIDER_AR_W;
      setSliderFit((prev) => {
        if (prev && Math.abs(prev.width - w) < 1 && Math.abs(prev.height - h) < 1) return prev;
        return { width: w, height: h };
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    const leftEl = leftColRef.current;
    const rightEl = rightColRef.current;
    if (leftEl) ro.observe(leftEl);
    if (rightEl) ro.observe(rightEl);
    return () => ro.disconnect();
  }, [a, isMobile, isTablet]);

  return (
    <>
      <div style={{ position: "relative", marginBottom: 28 }}>
        <div
          role="tablist"
          aria-label="Settori"
          style={{
            display: "flex",
            flexWrap: isMobile ? "nowrap" : "wrap",
            justifyContent: isMobile ? "flex-start" : "center",
            gap: 10,
            overflowX: isMobile ? "auto" : "visible",
            WebkitOverflowScrolling: "touch",
            padding: isMobile ? "4px 0 8px" : "0 0 8px",
            scrollbarWidth: "thin",
          }}
        >
          {tabs.map((tb, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              id={`${tabId}-${i}`}
              aria-selected={a === i}
              aria-controls={panelId}
              tabIndex={a === i ? 0 : -1}
              onClick={() => setA(i)}
              style={{
                flexShrink: 0,
                padding: "12px 22px",
                border: `1.5px solid ${a === i ? C.smartBlue : C.border}`,
                cursor: "pointer",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: a === i ? 700 : 500,
                fontFamily: F.h,
                background: a === i ? C.smartBlue20 : C.white,
                color: a === i ? C.textTitle : C.textNum,
                transition: `all 0.25s ${ease}`,
                boxShadow: a === i ? "0 4px 14px rgba(0,156,221,0.12)" : "none",
              }}
            >
              {tb.tag}
            </button>
          ))}
        </div>
        {isMobile && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 12,
              width: 44,
              pointerEvents: "none",
              background: `linear-gradient(90deg, rgba(255,255,255,0), ${C.white} 78%)`,
            }}
          />
        )}
      </div>
      <div style={{ background: C.white, borderRadius: C.radiusCard, padding: isMobile ? "24px 18px" : "32px 28px", boxShadow: C.shadow, border: `1px solid ${C.border}` }}>
        <div
          role="tabpanel"
          id={panelId}
          aria-labelledby={`${tabId}-${a}`}
          style={{ display: "grid", gridTemplateColumns: isMobile || isTablet ? "1fr" : "minmax(280px, 1fr) minmax(340px, 1.15fr)", gap: isMobile ? 24 : 32, alignItems: "start" }}
        >
          <div ref={leftColRef}>
            <p style={{ fontSize: 16, fontWeight: 400, color: C.textNum, fontFamily: F.b, lineHeight: "25.6px", marginBottom: 8 }}>Alcuni dei campi che OBSCURA riesce ad anonimizzare:</p>
            {/* <p style={{ fontSize: 12, lineHeight: "18px", fontWeight: 400, color: C.textNum, fontFamily: F.b, margin: "0 0 14px" }}>
              Non è un elenco chiuso: OBSCURA può rilevare anche altre tipologie. Nel contesto del settore selezionato, questi sono tra i dati che di solito meritano attenzione prima di passare il documento a un&apos;AI o a terzi.
            </p> */}
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", columnGap: isMobile ? 0 : 20, rowGap: 2 }}>
              {t.piiKeys.map((code) => (
                <li
                  key={code}
                  title={code}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    padding: "5px 0",
                    fontSize: 14,
                    lineHeight: "22px",
                    color: C.textNum,
                    fontFamily: F.b,
                    fontWeight: 400,
                    minWidth: 0,
                  }}
                >
                  <span style={{ flexShrink: 0, width: 20, display: "flex", justifyContent: "center", paddingTop: 2 }}>{icons.check}</span>
                  <span style={{ minWidth: 0 }}>{PII_LABELS_IT[code] || code}</span>
                </li>
              ))}
            </ul>
            <p style={{ fontSize: 16, fontWeight: 400, fontFamily: F.b, color: C.textNum, marginBottom: 8 }}>Quando serve:</p>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px" }}>
              {t.pts.map((p, i) => <li key={i} style={{ display: "flex", gap: 8, padding: "4px 0", fontSize: 16, lineHeight: "25.6px", color: C.textNum, fontFamily: F.b, fontWeight: 400 }}><span style={{ color: C.smartBlue, fontWeight: 700 }}>•</span>{p}</li>)}
            </ul>
            <GBtn onClick={onNavigate}>{t.cta}</GBtn>
          </div>
          <div ref={rightColRef} style={{ width: "88%", maxWidth: "100%", marginLeft: "auto", marginRight: "auto", justifySelf: "center", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
            <BeforeAfterPdfSlider key={a} beforeSrc={t.beforeImage} afterSrc={t.afterImage} isMobile={isMobile} fitSize={isMobile || isTablet ? null : sliderFit} />
          </div>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════
   API HELPERS
   ═══════════════════════════════════════════ */
function unwrapBody(res) { if (!res || typeof res !== "object") return {}; if (Array.isArray(res.jobs) && res.jobs.length > 0) return res; if (res.job_id || res.jobId) return res; const n = res.data || res.body || res.payload || res.result; if (n && typeof n === "object" && !Array.isArray(n)) return n; return res; }
function extractJobs(p) { const a = p.jobs || p.job_list || p.anonymization_jobs; if (Array.isArray(a) && a.length > 0) return a.map(e => typeof e === "string" ? { job_id: e } : { job_id: e.job_id || e.jobId || e.id, filename: e.filename || e.file_name }).filter(e => e.job_id); if (Array.isArray(p.job_ids)) return p.job_ids.filter(id => typeof id === "string").map(id => ({ job_id: id })); return []; }
// L'attesa dei job vive in src/jobPolling.js: la' e' testabile, qui dentro
// no (questo file importa pdf.js, che in jsdom non si carica). Le fabbriche
// dei messaggi restano qui e le vengono passate.
const pollJobPreviewUntilDone = (jobId, onStatus) =>
  pollJob(jobId, onStatus, { httpError, friendlyMessage });

/** Salva un blob su disco. Stesso gesto per il file singolo e per l'archivio. */
function saveBlob(blob, filename) {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(u);
}
function revokeResultPreviewUrls(rows) {
  if (!Array.isArray(rows)) return;
  for (const row of rows) {
    if (row?.previewUrl) {
      try { URL.revokeObjectURL(row.previewUrl); } catch (_) { /* noop */ }
    }
  }
}
async function fetchPaidJobDownloadPayload(jobId, sessionId) {
  if (!sessionId || typeof sessionId !== "string") {
    throw friendlyMessage("Sessione di pagamento mancante. Completa il checkout o ricarica la pagina dopo il pagamento.", "checkout");
  }
  const qs = new URLSearchParams({ session_id: sessionId.trim() });
  const r = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/download?${qs.toString()}`);
  if (!r.ok) {
    if (r.status === 401 || r.status === 403) throw httpError(r.status, "job");
    throw httpError(r.status, "job");
  }
  return r.json();
}
// La memoria del lavoro corrente sta in recupero.js: id dei lavori, sessione
// pagata se c'e', ora di fine. Questi nomi restano per i punti che li usano.
function savePendingCheckout(jobIds) {
  const esistente = leggiLavoro();
  salvaLavoro({ jobIds, sessionId: esistente?.sessionId || null, ts: esistente?.ts || Date.now() });
}
function readPendingCheckout() {
  return leggiLavoro()?.jobIds || null;
}
/** Dopo lo sblocco: la sessione pagata si ricorda, cosi' un aggiornamento
 *  della pagina non butta via i documenti finche' il motore li tiene. */
function ricordaSblocco(jobIds, sessionId) {
  const esistente = leggiLavoro();
  const ids = Array.isArray(jobIds) && jobIds.length ? jobIds : esistente?.jobIds || [];
  salvaLavoro({ jobIds: ids, sessionId, ts: esistente?.ts || Date.now() });
}
/** Pagine e importo del checkout aperto, per l'evento di conversione al
 *  ritorno da Stripe: su telefono si torna in una pagina nuova, senza stato
 *  in memoria. Sta nel tab (sessionStorage), non e' un dato personale. */
const CHIAVE_IMPORTO_CHECKOUT = "obscura_checkout_importo";
function ricordaImportoCheckout(pagine, importo) {
  try { window.sessionStorage.setItem(CHIAVE_IMPORTO_CHECKOUT, JSON.stringify({ pagine, importo })); } catch (_) { /* senza storage l'evento parte senza importo */ }
}
function leggiImportoCheckout() {
  try {
    const dati = JSON.parse(window.sessionStorage.getItem(CHIAVE_IMPORTO_CHECKOUT) || "null");
    return dati && typeof dati === "object" ? { pagine: dati.pagine, importo: dati.importo } : {};
  } catch (_) { return {}; }
}
function b64ToBlob(b, m) { const c = atob(b), a = new Uint8Array(c.length); for (let i = 0; i < c.length; i++) a[i] = c.charCodeAt(i); return new Blob([a], { type: m }); }
function getMime(f) { const e = f.split(".").pop()?.toLowerCase(); return { pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", csv: "text/csv", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", ppt: "application/vnd.ms-powerpoint" }[e] || "application/octet-stream"; }
function rasterPreviewSrcDoc(imageBlobUrl) {
  const src = String(imageBlobUrl).replace(/&/g, "&amp;").replace(/"/g, "&quot;");
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;width:100%;height:100%;background:#000;overflow:hidden}body{display:flex;align-items:center;justify-content:center;box-sizing:border-box;padding:12px}img{display:block;max-width:100%;max-height:100%;width:auto;height:auto;object-fit:contain;border:0}</style></head><body><img src="${src}" alt=""></body></html>`;
}
const previewFrameStyle = { position: "absolute", left: 0, top: 0, width: "100%", height: "100%", border: "none" };
const previewUnavailableStyle = { position: "absolute", left: 0, top: 0, right: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.5)", fontSize: 13, margin: 0, padding: "0 16px", textAlign: "center" };
function DocumentResultPreview({ result }) {
  if (!result.previewUrl) return <p style={previewUnavailableStyle}>Anteprima non disponibile</p>;
  const isPdf = /\.pdf$/i.test(result.redacted_filename);
  if (isPdf) {
    return (
      <PdfResultPreview
        blobUrl={result.previewUrl}
        title={result.redacted_filename}
        originalFilename={result.original_filename || ""}
      />
    );
  }
  return <iframe srcDoc={rasterPreviewSrcDoc(result.previewUrl)} style={previewFrameStyle} title={result.redacted_filename} />;
}
function parseResultPageCount(r) {
  if (!r || typeof r !== "object") return null;
  const inner = r.result && typeof r.result === "object" && !Array.isArray(r.result) ? r.result : null;
  const raw = r.page_count ?? r.pageCount ?? r.pages ?? r.num_pages ?? r.nb_pages
    ?? inner?.page_count ?? inner?.pageCount ?? inner?.pages ?? inner?.num_pages;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}
function rowFromApiResult(r, { previewOnly, obscuraJobId }) {
  const pCount = parseResultPageCount(r);
  const jid = obscuraJobId || r.obscura_job_id || null;
  const o = {
    original_filename: r.original_filename || r.originalFilename || "",
    redacted_filename: r.redacted_filename || r.redactedFilename || "redacted.pdf",
    download_url: r.download_url || "",
    content_base64: r.content || r.anonymized_base64 || null,
    page_count: pCount != null ? pCount : null,
    blob: null,
    previewUrl: null,
    previewOnly: !!previewOnly,
    obscura_job_id: typeof jid === "string" ? jid : null,
    // Solo il conteggio, mai il report. Serve a sapere QUALI documenti hanno
    // segnalazioni senza doverli aprire tutti; il report contiene i valori
    // originali e non deve entrare nello stato del frontend.
    //
    // E' un numero diverso da quello che mostra l'editor: qui sono le entita'
    // deduplicate del report, li' i gruppi di parole adiacenti a schermo. Per
    // questo sui documenti non ancora aperti si mostra un pallino e non una
    // cifra: due numeri diversi per la stessa cosa confonderebbero piu' di
    // quanto informino.
    //
    // Il backend toglie il report e lascia al suo posto `sospetti`, il solo
    // numero; la seconda strada e' per un motore interrogato direttamente.
    sospetti: Number(r?.sospetti ?? r?.report?.summary?.sospetti) || 0,
  };
  if (!o.download_url && o.content_base64) {
    o.blob = b64ToBlob(o.content_base64, getMime(o.redacted_filename));
    o.previewUrl = URL.createObjectURL(o.blob);
  }
  return o;
}
function resultMatchKeys(obj) {
  if (!obj || typeof obj !== "object") return [];
  const r = String(obj.redacted_filename || obj.redactedFilename || "").trim().toLowerCase();
  const o = String(obj.original_filename || obj.originalFilename || "").trim().toLowerCase();
  return [...new Set([r, o].filter(Boolean))];
}
function paidResultsByFilenameKey(paidArr) {
  const map = new Map();
  if (!Array.isArray(paidArr)) return map;
  for (const p of paidArr) {
    if (!p || typeof p !== "object") continue;
    for (const k of resultMatchKeys(p)) {
      if (!map.has(k)) map.set(k, p);
    }
  }
  return map;
}
function matchPaidItemsToRowIndices(snapshot, indices, paidArr) {
  if (!Array.isArray(paidArr) || paidArr.length < indices.length) return null;
  const paidByKey = paidResultsByFilenameKey(paidArr);
  const usedPaid = new Set();
  const out = new Map();
  for (const resultIdx of indices) {
    const row = snapshot[resultIdx];
    let item = null;
    for (const k of resultMatchKeys(row)) {
      const cand = paidByKey.get(k);
      if (cand && typeof cand === "object" && !usedPaid.has(cand)) {
        item = cand;
        break;
      }
    }
    if (item) {
      usedPaid.add(item);
      out.set(resultIdx, item);
    }
  }
  const remainingPaid = paidArr.filter((p) => p && typeof p === "object" && !usedPaid.has(p));
  const unresolved = indices.filter((i) => !out.has(i));
  if (unresolved.length > remainingPaid.length) return null;
  unresolved.forEach((resultIdx, j) => {
    out.set(resultIdx, remainingPaid[j]);
  });
  for (const i of indices) {
    const it = out.get(i);
    if (!it || typeof it !== "object") return null;
  }
  return out;
}
function statusToFriendlyMessage(status, context = "anonymize") {
  switch (status) {
    case 400:
      if (context === "contact") return "Alcuni campi del modulo non sono validi. Controllali e riprova.";
      if (context === "checkout") return PAGAMENTO_NON_AVVIATO;
      return "I file selezionati non sono validi. Controlla il contenuto e riprova.";
    case 401:
    case 403:
      return "Sessione scaduta o accesso non autorizzato. Aggiorna la pagina e riprova.";
    case 404:
      return "Servizio momentaneamente non disponibile. Riprova fra qualche istante.";
    case 408:
      return "Il server ha impiegato troppo tempo a rispondere. Riprova fra qualche istante.";
    case 413:
      if (context === "contact") return "Il messaggio inviato è troppo grande. Accorcialo e riprova.";
      if (context === "job") return "Risposta troppo grande dal servizio. Riprova fra qualche istante o con un file più leggero.";
      return "I file caricati superano il limite di caricamento. Riduci la dimensione o carica meno file alla volta.";
    case 415:
      return "Formato file non supportato. Carica solo file PDF, DOCX, CSV, PPT, PNG o JPG.";
    case 422:
      return "Uno o più file non possono essere elaborati. Verifica che non siano corrotti o protetti da password.";
    case 429:
      return "Troppe richieste in poco tempo. Attendi qualche secondo e riprova.";
    case 500:
    case 502:
    case 503:
    case 504:
      if (context === "checkout") return "Il servizio di pagamento è momentaneamente non disponibile. Riprova fra qualche minuto.";
      if (context === "contact") return "Invio del messaggio non riuscito al momento. Riprova fra qualche minuto.";
      return "Il servizio è momentaneamente non disponibile. Riprova fra qualche minuto.";
    default:
      if (status >= 500) return "Il servizio è momentaneamente non disponibile. Riprova fra qualche minuto.";
      if (status >= 400) return "Si è verificato un problema con la richiesta. Riprova o contattaci se il problema persiste.";
      return "Si è verificato un errore inatteso. Riprova fra qualche istante.";
  }
}
function httpError(status, context) {
  const e = new Error(statusToFriendlyMessage(status, context));
  e.status = status;
  e.context = context;
  e.isFriendly = true;
  return e;
}
function friendlyMessage(message, context) {
  const e = new Error(message);
  e.context = context;
  e.isFriendly = true;
  return e;
}
function isNetworkError(err) {
  if (!err) return false;
  if (err.name === "TypeError") return true;
  const m = String(err.message || "").toLowerCase();
  return /failed to fetch|network ?error|load failed|networkrequest|networkerror/i.test(m);
}
function toFriendlyError(err, context = "anonymize") {
  if (err?.isFriendly && err.message) return err.message;
  if (typeof err?.status === "number") return statusToFriendlyMessage(err.status, err.context || context);
  if (isNetworkError(err)) return "Connessione al server non riuscita. Verifica la tua connessione e riprova.";
  return "Si è verificato un errore inatteso. Riprova fra qualche istante.";
}
const PRICE_PER_PAGE_EUR = 0.1;
// Oltre questa dimensione un file non passa da Nginx: le sue pagine non le
// conta il server (/api/count-pages).
const LIMITE_CONTEGGIO_SERVER = 10 * 1024 * 1024;
// Documenti chiesti insieme al backend riaprendo un lavoro: lui prepara al
// massimo tre anteprime alla volta, chiederne di piu' le metterebbe in coda.
const DOCUMENTI_INSIEME = 3;
const MIN_CHARGE_EUR = 1;
const moneyFmt = new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" });

/* ═══════════════════════════════════════════
   ANONYMIZE PAGE (unchanged logic)
   ═══════════════════════════════════════════ */
function AnonymizePage({ onNavigateTo }) {
  const { isMobile, isTablet } = useViewport();
  // Con un abbonamento attivo non c'e' checkout: il backend risponde con i
  // documenti interi e conta le pagine sul piano. Le opzioni di piano
  // (pseudonimizzazione, firme) compaiono solo se il piano le include.
  const { account, caricamento: caricamentoAccount, ricarica: ricaricaAccount, esci: esciAccount, api: apiAccount } = useAccount();
  const abbonata = !!account?.azienda?.abbonata;
  const funzioni = abbonata && Array.isArray(account?.funzioni) ? account.funzioni : [];
  const [pseudonimizza, setPseudonimizza] = useState(false);
  const [sfocaFirme, setSfocaFirme] = useState(false);
  // Report di conformita' (Silver e superiori): il PDF dei lavori mostrati.
  const reportDisponibile = funzioni.includes("report");
  const [preparaReport, setPreparaReport] = useState(false);
  const pagePadding = isMobile ? "16px" : isTablet ? "20px" : "24px 32px";
  const [files, setFiles] = useState([]); const [dragging, setDragging] = useState(false);
  // Limiti di caricamento del piano, o di chi paga a consumo: li applica il
  // backend, qui si mostrano e si controllano prima di caricare per niente.
  const [limiti, setLimiti] = useState(LIMITI_PREDEFINITI);
  const idAccount = account?.utente?.id || null;
  useEffect(() => {
    let vivo = true;
    fetch("/api/limiti-caricamento", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivo) setLimiti(limitiValidi(d)); })
      .catch(() => { if (vivo) setLimiti(LIMITI_PREDEFINITI); });
    return () => { vivo = false; };
  }, [idAccount, abbonata]);
  const avvisoLimiti = useMemo(() => controllaLimiti(files, limiti), [files, limiti]);
  // I file rimasti fuori dall'ultimo caricamento perche' oltre il limite per
  // file: la frase resta finche' l'elenco non cambia o la si chiude.
  const [fileScartati, setFileScartati] = useState(null);
  const [selectedCats, setSelectedCats] = useState({}); const [expanded, setExpanded] = useState({});
  const [selectAll, setSelectAll] = useState(false); const [isAnonymizing, setIsAnonymizing] = useState(false);
  const [statusMessage, setStatusMessage] = useState(""); const [results, setResults] = useState([]);
  const [error, setError] = useState(null); const [isPaying, setIsPaying] = useState(false);
  const [checkoutStarted, setCheckoutStarted] = useState(false); const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [unlockingPaidAssets, setUnlockingPaidAssets] = useState(false);
  const [stripeCheckoutSessionId, setStripeCheckoutSessionId] = useState(null);
  // I lavori di questa pagina, dall'invio al motore: la barra laterale li
  // segna come "Aperto nella pagina" anche mentre sono in elaborazione.
  const [lavoroAperto, setLavoroAperto] = useState([]);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [pagesByFile, setPagesByFile] = useState(() => new Map());
  // Miniature dei file caricati (vedi miniature.js), per File.
  const [miniature, setMiniature] = useState(() => new Map());
  const [isCountingPages, setIsCountingPages] = useState(false);
  const [wordsToRedact, setWordsToRedact] = useState("");
  // Parole salvate (piani che le includono): la lista personale dell'utente.
  // Non si applica da sola: la casella parte spenta a ogni nuovo documento.
  const paroleSalvateAttive = funzioni.includes("dizionari");
  const [paroleSalvate, setParoleSalvate] = useState([]);
  const [usaParoleSalvate, setUsaParoleSalvate] = useState(false);
  useEffect(() => {
    if (!paroleSalvateAttive || !apiAccount?.parole) { setParoleSalvate([]); return undefined; }
    let vivo = true;
    apiAccount.parole()
      .then((r) => { if (vivo) setParoleSalvate(Array.isArray(r?.parole) ? r.parole : []); })
      .catch(() => { if (vivo) setParoleSalvate([]); });
    return () => { vivo = false; };
  }, [paroleSalvateAttive, apiAccount]);
  const [blurFaces, setBlurFaces] = useState(false);
  const [resultsViewTab, setResultsViewTab] = useState("preview");
  const [editorDocIndex, setEditorDocIndex] = useState(0);
  // Conteggio esatto per documento, noto solo dopo che l'editor l'ha aperto.
  // Prima di allora si sa soltanto SE ci sono segnalazioni (row.sospetti dal
  // report), e si mostra un pallino.
  const [sospettiPerDoc, setSospettiPerDoc] = useState({});
  // Quale anteprima e' aperta nell'elenco compatto (una alla volta: aprirle
  // tutte riporterebbe il problema che l'elenco risolve).
  const [anteprimaAperta, setAnteprimaAperta] = useState(null);
  // Revisione in corso per documento: l'editor viene smontato quando si
  // cambia file, quindi le modifiche vanno tenute qui o si perdono.
  const [statoEditor, setStatoEditor] = useState({});
  const registraStatoEditor = useCallback((idx, stato) => {
    setStatoEditor((prev) => ({ ...prev, [idx]: stato }));
  }, []);
  const registraSospetti = useCallback((idx, n) => {
    setSospettiPerDoc((prev) => (prev[idx] === n ? prev : { ...prev, [idx]: n }));
  }, []);
  const fileInputRef = useRef(null);
  // Vero appena un ripristino (da URL di ritorno, da link di recupero o dalla
  // memoria locale) e' stato avviato: ne parte uno solo.
  const ripristinoRef = useRef(false);
  // Barra laterale di chi ha un account (si porta dietro i suoi dati, vedi
  // Sidebar.jsx). Su telefono si apre a richiesta.
  const [sidebarAperta, setSidebarAperta] = useState(false);
  // Same-tab checkout return (mobile): in-memory results are gone, so rebuild the
  // paid documents from the stored job IDs + Stripe session id.
  const restorePaidResultsFromJobs = useCallback(async (jobIds, sessionId) => {
    if (!Array.isArray(jobIds) || !jobIds.length || !sessionId) return;
    setUnlockingPaidAssets(true);
    setError(null);
    setStatusMessage("Recupero dei documenti dopo il pagamento...");
    try {
      // Tutti i documenti insieme (al massimo DOCUMENTI_INSIEME), non uno
      // dopo l'altro; l'ordine resta quello dei lavori.
      const esiti = await inParallelo(jobIds, DOCUMENTI_INSIEME, (jobId) => fetchPaidJobDownloadPayload(jobId, sessionId));
      const fallito = esiti.find((e) => !e.ok);
      if (fallito) throw fallito.errore;
      const rows = [];
      esiti.forEach(({ valore: payload }, i) => {
        const paidArr = Array.isArray(payload?.result?.results) ? payload.result.results : [];
        for (const item of paidArr) {
          rows.push(rowFromApiResult(item, { previewOnly: false, obscuraJobId: jobIds[i] }));
        }
      });
      if (!rows.length) {
        throw friendlyMessage("Impossibile recuperare i file dopo il pagamento. Riprova o contattaci.", "job");
      }
      setResults(rows);
      setStatusMessage("Pagamento confermato. Download sbloccato.");
      ricordaSblocco(jobIds, sessionId);
    } catch (e) {
      setError(toFriendlyError(e, "job"));
    } finally {
      setUnlockingPaidAssets(false);
    }
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");
    const sessionIdFromUrl = params.get("session_id");
    const hasSession = !!sessionIdFromUrl;
    const inPopup = !!window.opener && window.opener !== window;
    if (inPopup && (paymentStatus === "success" || paymentStatus === "cancel")) {
      window.opener.postMessage({ type: "obscura-checkout", status: paymentStatus, session_id: sessionIdFromUrl || null }, window.location.origin);
      window.close();
      return;
    }
    const recupero = params.get("recupero");
    if (paymentStatus === "success" && hasSession) {
      ripristinoRef.current = true;
      setCheckoutStarted(true);
      setIsPaymentComplete(true);
      setStripeCheckoutSessionId(sessionIdFromUrl);
      setStatusMessage("Pagamento confermato. Download sbloccato.");
      aggiornaSessioneLavoro(sessionIdFromUrl);
      traccia.pagamentoRiuscito({ sessione: sessionIdFromUrl, ...leggiImportoCheckout() });
      // After a full-page (same-tab) return there are no in-memory results to unlock,
      // so rebuild them from the persisted job IDs.
      const pendingJobIds = readPendingCheckout();
      if (pendingJobIds && pendingJobIds.length) {
        restorePaidResultsFromJobs(pendingJobIds, sessionIdFromUrl);
      }
    } else if (paymentStatus === "cancel") {
      setCheckoutStarted(true);
      setStatusMessage("Pagamento annullato. Puoi riprovare quando vuoi.");
    } else if (recupero) {
      // Link di recupero dall'email dopo il pagamento: la sessione pagata dice
      // al backend quali lavori ha sbloccato, e da li' si ricostruisce tutto.
      ripristinoRef.current = true;
      (async () => {
        try {
          const r = await fetch(`/api/pagamenti/${encodeURIComponent(recupero)}/lavori`);
          if (!r.ok) throw httpError(r.status, "job");
          const dati = await r.json();
          const ids = Array.isArray(dati?.job_ids) ? dati.job_ids : [];
          if (!ids.length) throw friendlyMessage("Questo link non corrisponde a nessun documento.", "job");
          salvaLavoro({ jobIds: ids, sessionId: recupero, ts: leggiLavoro()?.ts || Date.now() });
          setCheckoutStarted(true);
          setIsPaymentComplete(true);
          setStripeCheckoutSessionId(recupero);
          await restorePaidResultsFromJobs(ids, recupero);
        } catch (e) {
          setError(toFriendlyError(e, "job"));
        }
      })();
    }
    if (params.get("payment") || params.get("session_id") || recupero) {
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete("payment");
      cleaned.searchParams.delete("session_id");
      cleaned.searchParams.delete("recupero");
      window.history.replaceState({}, document.title, `${cleaned.pathname}${cleaned.search}`);
    }
  }, [restorePaidResultsFromJobs]);
  useEffect(() => {
    const onCheckoutMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (data.type !== "obscura-checkout") return;
      setCheckoutStarted(true);
      setIsPaying(false);
      if (data.status === "success") {
        setIsPaymentComplete(true);
        setError(null);
        if (typeof data.session_id === "string" && data.session_id.trim()) {
          setStripeCheckoutSessionId(data.session_id.trim());
          aggiornaSessioneLavoro(data.session_id.trim());
          traccia.pagamentoRiuscito({ sessione: data.session_id.trim(), ...leggiImportoCheckout() });
        }
        setStatusMessage("Pagamento confermato. Download sbloccato.");
      } else if (data.status === "cancel") {
        setIsPaymentComplete(false);
        setStripeCheckoutSessionId(null);
        setStatusMessage("Pagamento annullato. Puoi riprovare quando vuoi.");
      }
    };
    window.addEventListener("message", onCheckoutMessage);
    return () => window.removeEventListener("message", onCheckoutMessage);
  }, []);
  // Ripristino dopo un aggiornamento della pagina: finche' il motore tiene i
  // lavori (circa un'ora) la pagina si ricostruisce da sola. Con la sessione
  // pagata tornano i documenti interi, altrimenti le anteprime. Nel browser
  // non c'e' nessun documento: solo gli identificativi.
  const restorePreviewResultsFromJobs = useCallback(async (jobIds) => {
    setUnlockingPaidAssets(true);
    setError(null);
    setStatusMessage("Recupero dei documenti della sessione precedente...");
    let spariti = false;
    try {
      // Le anteprime si preparano al momento, una per documento: chieste
      // tutte insieme (al massimo DOCUMENTI_INSIEME) l'attesa e' quella del
      // documento piu' lento, non la somma. L'ordine resta quello dei lavori.
      const esiti = await inParallelo(jobIds, DOCUMENTI_INSIEME, async (jobId) => {
        const r = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/preview`);
        if (r.status === 404) return { sparito: true };
        if (!r.ok) throw httpError(r.status, "job");
        return { payload: await r.json() };
      });
      if (esiti.some((e) => e.ok && e.valore.sparito)) {
        spariti = true;
        throw friendlyMessage("Questi documenti non sono più disponibili: vengono eliminati circa un'ora dopo l'anonimizzazione. Rilancia l'anonimizzazione.", "job");
      }
      const fallito = esiti.find((e) => !e.ok);
      if (fallito) throw fallito.errore;
      const rows = [];
      esiti.forEach(({ valore: { payload } }, i) => {
        // I lavori non riusciti (o non ancora pronti) si saltano.
        if (payload?.status !== "completed") return;
        const arr = Array.isArray(payload?.result?.results) ? payload.result.results : [];
        for (const item of arr) rows.push(rowFromApiResult(item, { previewOnly: !abbonata, obscuraJobId: jobIds[i] }));
      });
      if (!rows.length) throw friendlyMessage("Nessun documento da recuperare.", "job");
      setResults(rows);
      setIsPaymentComplete(abbonata);
      setStatusMessage(abbonata
        ? "Documenti recuperati dalla sessione precedente: pronti per il download."
        : "Documenti recuperati dalla sessione precedente. Completa l'acquisto per scaricarli.");
    } catch (e) {
      // Se il motore non li ha piu' escono anche dall'elenco della barra
      // laterale, che altrimenti proporrebbe di riaprirli.
      if (spariti) dimenticaDallaCronologia(jobIds);
      dimenticaLavoro();
      setError(toFriendlyError(e, "job"));
    } finally {
      setUnlockingPaidAssets(false);
    }
  }, [abbonata]);
  // "Riapri" dalla barra laterale: il lavoro scelto diventa quello corrente e
  // la pagina lo ricostruisce sul posto, come dopo un aggiornamento.
  const riapriLavoro = useCallback(async (voce) => {
    if (!voce?.jobIds?.length) return;
    setSidebarAperta(false);
    setResults((prev) => { revokeResultPreviewUrls(prev); return []; });
    setFiles([]); setError(null); setFileScartati(null); setStatusMessage("");
    setResultsViewTab("preview"); setEditorDocIndex(0); setSospettiPerDoc({}); setAnteprimaAperta(null); setStatoEditor({});
    salvaLavoro(voce);
    setLavoroAperto(voce.jobIds);
    ripristinoRef.current = true;
    window.scrollTo(0, 0);
    if (voce.sessionId) {
      setCheckoutStarted(true); setIsPaymentComplete(true); setStripeCheckoutSessionId(voce.sessionId);
      await restorePaidResultsFromJobs(voce.jobIds, voce.sessionId);
    } else {
      setCheckoutStarted(false); setIsPaymentComplete(false); setStripeCheckoutSessionId(null);
      await restorePreviewResultsFromJobs(voce.jobIds);
    }
  }, [restorePaidResultsFromJobs, restorePreviewResultsFromJobs]);
  // Arrivando da un'altra pagina con "Riapri" nella barra laterale il lavoro
  // scelto e' in attesa nella memoria della scheda e si apre una volta sola.
  // Per il resto la pagina parte sempre da un documento nuovo, anche dopo un
  // aggiornamento: quello che si e' fatto prima resta nella barra laterale.
  useEffect(() => {
    if (caricamentoAccount || ripristinoRef.current || results.length) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") || params.get("session_id") || params.get("recupero")) return;
    // Link dell'email "documenti pronti" degli abbonati: i lavori sono
    // nell'indirizzo, che si ripulisce subito (un aggiornamento non li
    // riapre). Senza accesso si passa dal login: la richiesta aspetta nella
    // memoria della scheda, come un "Riapri" da un'altra pagina.
    const daEmail = voceDaLinkEmail(params);
    if (daEmail) {
      const pulito = new URL(window.location.href);
      pulito.searchParams.delete("lavori");
      pulito.searchParams.delete("dal");
      window.history.replaceState({}, document.title, `${pulito.pathname}${pulito.search}`);
      if (!account) {
        chiediRiapertura(daEmail);
        // Un altro giro di questo effetto, prima che la pagina cambi, si
        // mangerebbe la richiesta appena messa da parte per dopo l'accesso.
        ripristinoRef.current = true;
        if (onNavigateTo) onNavigateTo("accesso", { next: "anonymize" });
        return;
      }
      riapriLavoro(daEmail);
      return;
    }
    const voce = prendiRiapertura();
    if (voce) riapriLavoro(voce);
  }, [caricamentoAccount, results.length, riapriLavoro, account, onNavigateTo]);
  useEffect(() => {
    if (!isPaymentComplete || !results.length) return;
    if (!results.some((row) => row.previewOnly && row.obscura_job_id)) return;
    if (!stripeCheckoutSessionId) return;

    let cancelled = false;
    setUnlockingPaidAssets(true);
    setError(null);
    setStatusMessage("Recupero dei documenti dopo il pagamento...");

    (async () => {
      try {
        const snapshot = results;
        revokeResultPreviewUrls(snapshot);

        const byJob = new Map();
        snapshot.forEach((row, idx) => {
          if (!row.previewOnly || !row.obscura_job_id) return;
          const id = row.obscura_job_id;
          if (!byJob.has(id)) byJob.set(id, []);
          byJob.get(id).push(idx);
        });

        const nextRows = snapshot.map((row) => ({ ...row }));

        // I documenti pagati arrivano insieme (al massimo DOCUMENTI_INSIEME),
        // poi si abbinano alle righe nell'ordine di sempre.
        const voci = Array.from(byJob.entries());
        const esiti = await inParallelo(voci, DOCUMENTI_INSIEME, ([jobId]) => fetchPaidJobDownloadPayload(jobId, stripeCheckoutSessionId));
        if (cancelled) return;
        const fallito = esiti.find((e) => !e.ok);
        if (fallito) throw fallito.errore;

        for (const [n, [jobId, indices]] of voci.entries()) {
          const payload = esiti[n].valore;
          const paidArr = Array.isArray(payload?.result?.results) ? payload.result.results : [];
          const match = matchPaidItemsToRowIndices(snapshot, indices, paidArr);
          if (!match) {
            throw friendlyMessage("Impossibile recuperare i file dopo il pagamento. Riprova o contattaci.", "job");
          }
          for (const resultIdx of indices) {
            const paidItem = match.get(resultIdx);
            nextRows[resultIdx] = rowFromApiResult(paidItem, { previewOnly: false, obscuraJobId: jobId });
          }
        }

        if (!cancelled) {
          setResults(nextRows);
          setStatusMessage("Pagamento confermato. Download sbloccato.");
          ricordaSblocco(Array.from(byJob.keys()), stripeCheckoutSessionId);
        }
      } catch (e) {
        if (!cancelled) setError(toFriendlyError(e, "job"));
      } finally {
        if (!cancelled) setUnlockingPaidAssets(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isPaymentComplete, results, stripeCheckoutSessionId]);
  const fetchPageCounts = useCallback(async (newFiles) => {
    if (!newFiles.length) return;
    setIsCountingPages(true);
    try {
      const fd = new FormData();
      for (const f of newFiles) fd.append("files", f, f.name);
      const res = await fetch("/api/count-pages", { method: "POST", body: fd });
      if (!res.ok) throw new Error(`count-pages ${res.status}`);
      const data = await res.json();
      const arr = Array.isArray(data?.files) ? data.files : [];
      setPagesByFile(prev => {
        const next = new Map(prev);
        newFiles.forEach((f, i) => {
          const pages = Math.max(1, Number(arr[i]?.pages) || 1);
          next.set(f, pages);
        });
        return next;
      });
    } catch (e) {
      setPagesByFile(prev => {
        const next = new Map(prev);
        newFiles.forEach(f => { if (!next.has(f)) next.set(f, 1); });
        return next;
      });
    } finally {
      setIsCountingPages(false);
    }
  }, []);
  const handleFiles = useCallback((nf) => {
    // Un file oltre il limite per file non entra nell'elenco: non si potrebbe
    // mai inviare. Gli avvisi di prima riguardavano l'elenco di prima.
    const { ammessi: arr, scartati } = separaTroppoGrandi(nf, limiti);
    setFileScartati(messaggioScartati(scartati, limiti));
    setError(null);
    if (!arr.length) return;
    setFiles(p => [...p, ...arr]);
    // Le pagine dei PDF le conta il browser mentre disegna la miniatura: i
    // file grandi non devono fare un viaggio in piu' verso il server solo per
    // essere contati. DOCX, PPTX e CSV le stima il backend, se passano da Nginx.
    const contaSulServer = (lista) => fetchPageCounts(lista.filter((f) => f.size <= LIMITE_CONTEGGIO_SERVER));
    contaSulServer(arr.filter((f) => !["pdf", "immagine"].includes(tipoFile(f.name))));
    // Miniature: PDF e immagini si disegnano nel browser; per gli altri
    // formati la scheda mostra il tipo (vedi miniature.js).
    for (const f of arr) {
      if (!["pdf", "immagine"].includes(tipoFile(f.name))) continue;
      generaMiniatura(f)
        .then((m) => {
          setMiniature((prev) => new Map(prev).set(f, m));
          if (Number.isFinite(m?.pagine) && m.pagine > 0) setPagesByFile((prev) => new Map(prev).set(f, m.pagine));
          else contaSulServer([f]);
        })
        .catch(() => contaSulServer([f]));
    }
  }, [fetchPageCounts, limiti]);
  const dimenticaMiniature = (quali) => setMiniature((prev) => {
    const next = new Map(prev);
    const lista = quali || [...prev.keys()];
    for (const f of lista) { revocaMiniatura(prev.get(f)); next.delete(f); }
    return next;
  });
  const handleDrop = useCallback((e) => { e.preventDefault(); setDragging(false); if (!privacyAccepted) return; handleFiles(e.dataTransfer.files); }, [handleFiles, privacyAccepted]);
  // Cambiato l'elenco, gli avvisi sull'elenco di prima non valgono piu': prima
  // l'errore copiato da "Anonimizza" restava anche a elenco vuoto.
  const removeFile = (i) => {
    setFileScartati(null); setError(null);
    setFiles(p => {
      const removed = p[i];
      if (removed) {
        setPagesByFile(prev => { const n = new Map(prev); n.delete(removed); return n; });
        dimenticaMiniature([removed]);
      }
      return p.filter((_, j) => j !== i);
    });
  };
  const removeAll = () => { setFileScartati(null); setError(null); setFiles([]); setPagesByFile(new Map()); dimenticaMiniature(null); };
  const toggleCat = (n) => { setSelectedCats(p => { const c = CATEGORIES.find(x => x.name === n); const all = c.items.every(i => p[i]); const nx = { ...p }; c.items.forEach(i => { nx[i] = !all; }); return nx; }); };
  const toggleItem = (i) => setSelectedCats(p => ({ ...p, [i]: !p[i] }));
  const toggleSelectAll = () => { const nv = !selectAll; setSelectAll(nv); const nx = {}; if (nv) CATEGORIES.forEach(c => c.items.forEach(i => { nx[i] = true; })); setSelectedCats(nx); };
  const countSelected = (c) => c.items.filter(i => selectedCats[i]).length;
  const reset = () => {
    dimenticaLavoro();
    revokeResultPreviewUrls(results);
    setFiles([]); setSelectedCats({}); setSelectAll(false); setResults([]);
    setError(null); setFileScartati(null); setStatusMessage(""); setCheckoutStarted(false); setIsPaymentComplete(false);
    setLavoroAperto([]);
    setUnlockingPaidAssets(false);
    setStripeCheckoutSessionId(null);
    setPrivacyAccepted(false);
    setPagesByFile(new Map());
    dimenticaMiniature(null);
    setWordsToRedact("");
    setUsaParoleSalvate(false);
    setBlurFaces(false);
    setResultsViewTab("preview");
    setEditorDocIndex(0);
    setSospettiPerDoc({});
    setAnteprimaAperta(null);
    setStatoEditor({});
  };
  const getSelectedCodes = () => Object.entries(selectedCats).filter(([_, v]) => v).map(([k]) => k);
  const getWordsToRedact = () => [...new Set(wordsToRedact.split(/[\n,]/).map(w => w.trim()).filter(Boolean))];
  const pagesForResult = (r, idx) => {
    const parsed = parseResultPageCount(r);
    if (parsed != null) return parsed;
    const fromStored = Number(r?.page_count);
    if (Number.isFinite(fromStored) && fromStored > 0) return fromStored;
    let match = r?.original_filename ? files.find(f => f.name === r.original_filename) : null;
    if (!match && r?.redacted_filename) {
      const stripped = r.redacted_filename.replace(/_(anonimizzato|redacted)(?=\.[^.]+$)/i, "");
      match = files.find(f => f.name === stripped);
    }
    if (!match && typeof idx === "number" && files[idx]) match = files[idx];
    if (match && pagesByFile.has(match)) return pagesByFile.get(match);
    return 1;
  };
  const uploadedTotalPages = files.reduce((sum, f) => sum + (pagesByFile.get(f) || 1), 0);
  const resultsTotalPages = results.reduce((sum, r, i) => sum + pagesForResult(r, i), 0);
  const totalPages = results.length > 0 ? resultsTotalPages : uploadedTotalPages;
  const totalCost = Math.max(totalPages * PRICE_PER_PAGE_EUR, MIN_CHARGE_EUR);

  const startAnonymization = async () => {
    const codes = getSelectedCodes();
    const words = getWordsToRedact();
    if (!files.length) { setError("Nessun file caricato."); return; }
    if (avvisoLimiti) { setError(avvisoLimiti); return; }
    const conParoleSalvate = usaParoleSalvate && paroleSalvateAttive && paroleSalvate.length > 0;
    if (!codes.length && !words.length && !conParoleSalvate) { setError("Seleziona almeno una categoria o inserisci parole specifiche da anonimizzare."); return; }
    if (!privacyAccepted) { setError("È necessario accettare l'informativa privacy prima di procedere."); return; }
    setIsAnonymizing(true); setError(null); setStatusMessage("Invio file..."); setResults((prev) => { revokeResultPreviewUrls(prev); return []; });
    setLavoroAperto([]);
    traccia.anonimizzazioneAvviata({ documenti: files.length, pagine: uploadedTotalPages, abbonato: abbonata });
    try {
      const fd = new FormData();
      fd.append("categories_list", JSON.stringify(files.map(() => codes)));
      fd.append("words_to_redact", JSON.stringify(words));
      // La lista la legge il backend: qui basta dire che va usata.
      if (conParoleSalvate) fd.append("parole_salvate", "true");
      fd.append("faces", blurFaces ? "true" : "false");
      if (abbonata && pseudonimizza && funzioni.includes("pseudonimizzazione")) fd.append("mode", "pseudonym");
      if (abbonata && sfocaFirme && funzioni.includes("firme")) fd.append("signatures", "true");
      // Caricamento diretto: i file vanno su S3 e qui passano solo le chiavi.
      // Se nel frattempo il backend lo ha spento (501), la strada di sempre.
      let caricatiSuS3 = false;
      if (limiti.diretto) {
        try {
          setStatusMessage("Caricamento dei file: 0%");
          const voci = await caricaSuS3(files, { onAvanzamento: (q) => setStatusMessage(`Caricamento dei file: ${Math.round(q * 100)}%`) });
          fd.append("chiavi", JSON.stringify(voci.map((v, i) => ({ ...v, pagine: pagesByFile.get(files[i]) || 1 }))));
          caricatiSuS3 = true;
        } catch (err) {
          if (err?.status !== 501) throw err;
        }
      }
      if (!caricatiSuS3) for (const f of files) fd.append("files", f, f.name);
      const sr = await fetch("/api/anonymize", { method: "POST", body: fd });
      if (!sr.ok) {
        // Il backend dice quale limite e' stato superato: meglio del messaggio generico.
        const corpo = sr.status === 413 ? await sr.json().catch(() => null) : null;
        if (typeof corpo?.detail === "string") throw friendlyMessage(corpo.detail, "anonymize");
        throw httpError(sr.status, "anonymize");
      }
      const pl = await sr.json(); const uw = unwrapBody(pl); const je = extractJobs(uw); const si = uw.job_id || uw.jobId || null;
      // Il motore ha i file: il lavoro entra subito nei "Lavori recenti", in
      // elaborazione. Chi chiude la scheda lo ritrova li' e lo riapre quando
      // e' pronto; la barra laterale ne segue lo stato.
      const inviati = je.length
        ? je.map((j) => j.job_id).filter((id) => typeof id === "string" && id)
        : (typeof si === "string" && si ? [si] : []);
      if (inviati.length) {
        salvaLavoro({ jobIds: inviati, nomi: files.map((f) => f.name), inCorso: true });
        setLavoroAperto(inviati);
      }
      let ar = [];
      if (je.length > 0) {
        // Con piu' file il worker lavora in sequenza e l'attesa si somma: una
        // rotellina indistinta per minuti non dice se qualcosa stia
        // avanzando. I cicli di attesa sanno gia' chi e' pronto, basta
        // contarli. Il messaggio lo gestisce il chiamante, quindi ai singoli
        // cicli non si passa onStatus: si sovrascriverebbero a vicenda.
        const totali = je.length;
        let fatti = 0;
        setStatusMessage(`Anonimizzazione: 0 di ${totali} completati...`);
        const s = await Promise.allSettled(je.map((j) =>
          pollJobPreviewUntilDone(j.job_id).then((res) => {
            fatti += 1;
            setStatusMessage(`Anonimizzazione: ${fatti} di ${totali} completati...`);
            return res;
          })
        ));
        for (const r of s) if (r.status === "fulfilled") ar.push(...r.value);
      }
      else if (si) { setStatusMessage("Anonimizzazione..."); ar = await pollJobPreviewUntilDone(si, setStatusMessage); }
      else if (Array.isArray(uw.results)) ar = uw.results;
      if (!ar.length) throw friendlyMessage("Non è stato possibile elaborare i file caricati. Verifica che siano leggibili e riprova.", "anonymize");
      setResults(ar.map((r) => {
        const jid = typeof r?.obscura_job_id === "string" ? r.obscura_job_id : "";
        const fromPreviewPipe = jid.length > 0;
        const { obscura_job_id: _strip, ...rest } = typeof r === "object" && r ? r : {};
        // Con un abbonamento il polling riceve gia' il documento intero:
        // niente anteprima sfocata da sostituire, niente checkout.
        return rowFromApiResult(rest, { previewOnly: !abbonata && fromPreviewPipe, obscuraJobId: fromPreviewPipe ? jid : null });
      }));
      // Da qui un aggiornamento della pagina non perde piu' niente: i lavori
      // si ricordano e si rileggono dal motore finche' li tiene. La voce
      // dell'invio diventa pronta, con l'ora della fine e i nomi dei file
      // come li ha scelti l'utente. Se un file non e' riuscito la voce tiene
      // solo i lavori pronti, gli stessi che il pagamento sblocchera':
      // altrimenti dopo l'acquisto l'elenco ne mostrerebbe due.
      const pronti = [...new Set(ar.map((r) => r?.obscura_job_id).filter((id) => typeof id === "string" && id))];
      if (inviati.length && pronti.length) {
        const nomeDi = new Map(inviati.map((id, i) => [id, je.length ? files[i]?.name : null]));
        if (chiaveLavori(pronti) !== chiaveLavori(inviati)) dimenticaDallaCronologia(inviati);
        salvaLavoro({ jobIds: pronti, nomi: je.length ? pronti.map((id) => nomeDi.get(id)).filter(Boolean) : files.map((f) => f.name) });
        setLavoroAperto(pronti);
      } else {
        salvaLavoro({
          jobIds: pronti,
          nomi: ar.map((r) => r?.original_filename || r?.originalFilename || r?.redacted_filename || r?.redactedFilename).filter(Boolean),
        });
      }
      setCheckoutStarted(false); setIsPaymentComplete(abbonata);
      setStripeCheckoutSessionId(null);
      setStatusMessage(abbonata ? "Completato: documenti pronti, pagine comprese nel piano." : "Completato!");
      traccia.anonimizzazioneCompletata({ documenti: ar.length, abbonato: abbonata });
      // Rileggere il profilo aggiorna anche la barra laterale (consumo e lavori).
      if (abbonata) ricaricaAccount();
    } catch (e) {
      setError(toFriendlyError(e, "anonymize")); setStatusMessage("");
      // Se il motore lo sta ancora lavorando, la barra laterale lo segue e lo
      // propone da riaprire quando e' pronto: qui non e' piu' aperto.
      setLavoroAperto([]);
    } finally { setIsAnonymizing(false); }
  };
  const startCheckout = async () => {
    if (!results.length) return;
    setIsPaying(true); setError(null);
    setStripeCheckoutSessionId(null);
    try {
      const checkoutJobIds = [...new Set(results.map((r) => r.obscura_job_id).filter((id) => typeof id === "string" && String(id).trim()))];
      if (checkoutJobIds.length === 0) {
        throw friendlyMessage("Impossibile avviare il checkout: manca il riferimento al job. Rielabora i file e riprova.", "checkout");
      }
      const primaryJobId = checkoutJobIds[0];
      const checkoutEndpoint = `/api/jobs/${encodeURIComponent(primaryJobId)}/checkout`;
      const res = await fetch(checkoutEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nb_pages: totalPages,
          amount_eur: totalCost,
          job_ids: checkoutJobIds,
        }),
      });
      if (!res.ok) {
        // Documenti spariti o elaborazione non conclusa: il backend dice
        // gia' quale dei due, meglio del messaggio generico.
        const motivo = motivoCheckout(res.status, await res.json().catch(() => null));
        if (motivo) throw friendlyMessage(motivo, "checkout");
        throw httpError(res.status, "checkout");
      }
      const body = await res.json();
      const openUrl = body?.url || body?.checkout_url;
      if (!openUrl) throw friendlyMessage("Impossibile avviare il pagamento al momento. Riprova fra qualche istante.", "checkout");
      setCheckoutStarted(true);
      // Persist the job IDs so we can rebuild the paid documents on return, even if
      // the in-memory state is lost (e.g. a same-tab redirect on mobile).
      savePendingCheckout(checkoutJobIds);
      ricordaImportoCheckout(totalPages, totalCost);
      traccia.checkoutAperto({ pagine: totalPages, importo: totalCost });
      if (isMobile) {
        // On mobile the popup-courier pattern is unreliable (new tabs can't auto-close
        // on iOS), which strands the user on a fresh upload page. Redirect this tab and
        // rebuild results from the stored job IDs when Stripe returns to the success URL.
        setStatusMessage("Reindirizzamento al pagamento sicuro...");
        window.location.assign(openUrl);
        return;
      }
      const checkoutWindow = window.open(openUrl, "obscuraCheckout", "width=560,height=760");
      if (!checkoutWindow) {
        throw friendlyMessage("Popup bloccato dal browser. Abilita i popup e riprova.", "checkout");
      }
      checkoutWindow.focus();
      setStatusMessage("Checkout aperto in popup. Completa il pagamento per sbloccare i download.");
    } catch (e) {
      setError(toFriendlyError(e, "checkout"));
    } finally {
      setIsPaying(false);
    }
  };
  const downloadResult = async (r) => {
    if (!isPaymentComplete) { setError("Completa il pagamento per scaricare i file."); return; }
    if (unlockingPaidAssets) { setError("Download in preparazione dopo il pagamento. Attendi un attimo."); return; }
    try {
      let b;
      if (r.blob) b = r.blob;
      else if (r.content_base64) b = b64ToBlob(r.content_base64, getMime(r.redacted_filename));
      else if (r.download_url) {
        const res = await fetch(r.download_url.startsWith("http") ? r.download_url : `/api${r.download_url}`);
        if (!res.ok) throw httpError(res.status, "download");
        b = await res.blob();
      }
      if (!b) {
        setError("Download non disponibile per questo file. Riprova fra qualche istante.");
        return;
      }
      saveBlob(b, r.redacted_filename);
    } catch (e) {
      setError(toFriendlyError(e, "download"));
    }
  };
  const downloadAll = async () => {
    if (!isPaymentComplete) { setError("Completa il pagamento per scaricare i file."); return; }
    if (unlockingPaidAssets) { setError("Download in preparazione dopo il pagamento. Attendi un attimo."); return; }
    if (docsModificati.length) {
      const n = docsModificati.length;
      const ok = window.confirm(`${n === 1 ? "Un documento ha" : `${n} documenti hanno`} modifiche fatte nell'editor e non ancora applicate. Scaricare comunque la versione precedente?`);
      if (!ok) return;
    }

    // Un archivio solo invece di N salvataggi: i browser bloccano i download
    // multipli automatici dopo il primo o il secondo, quindi con dieci
    // documenti l'utente ne otteneva uno.
    //
    // L'archivio si costruisce QUI e non sul server: dopo lo sblocco i
    // documenti sono gia' in memoria (r.blob / r.content_base64), quindi
    // chiederli al backend significherebbe rimandarli su e giu' per niente e
    // dipendere dalla sessione Stripe, che puo' mancare - era il motivo per
    // cui il primo tentativo ripiegava sempre sui download singoli.
    if (results.length > 1) {
      try {
        const usati = new Set();
        const voci = [];
        for (const r of results) {
          let b = r.blob;
          if (!b && r.content_base64) b = b64ToBlob(r.content_base64, getMime(r.redacted_filename));
          if (!b && r.download_url) {
            const res = await fetch(r.download_url.startsWith("http") ? r.download_url : `/api${r.download_url}`);
            if (res.ok) b = await res.blob();
          }
          if (!b) continue;
          voci.push({
            nome: nomeUnico(r.redacted_filename || "documento.pdf", usati),
            dati: new Uint8Array(await b.arrayBuffer()),
          });
        }
        if (voci.length !== results.length) {
          throw new Error(`archivio incompleto: ${voci.length} di ${results.length}`);
        }
        saveBlob(creaZip(voci), "documenti_anonimizzati.zip");
        return;
      } catch (e) {
        // Ripiego sui download singoli, ma detto: prima l'errore finiva in
        // console e l'utente vedeva solo partire N salvataggi senza sapere
        // perche'.
        console.warn("Archivio non riuscito, ripiego sui download singoli", e);
        setError("Non è stato possibile creare l'archivio: i file partono singolarmente. Se il browser ne blocca qualcuno, scaricali uno alla volta.");
      }
    }
    await Promise.allSettled(results.map((r) => downloadResult(r)));
  };
  const scaricaReport = async () => {
    if (preparaReport) return;
    setPreparaReport(true);
    setError(null);
    try {
      const { blob, nome } = await chiediReportConformita(results.map((r) => r.obscura_job_id));
      saveBlob(blob, nome);
    } catch (e) {
      setError(e.message);
    } finally {
      setPreparaReport(false);
    }
  };
  const hasFiles = files.length > 0; const hasResults = results.length > 0;
  // Documenti che si stanno ricostruendo (Riapri, ritorno dal pagamento) e
  // che ancora non ci sono: al posto della schermata vuota, la rotellina.
  const ripristinoInCorso = unlockingPaidAssets && !hasResults;
  // Passo corrente della barra in alto. Derivato dagli stati che esistono
  // gia', non da un contatore a parte: cosi' la barra non puo' finire a
  // raccontare una fase diversa da quella che l'utente ha davanti.
  const passoCorrente = isAnonymizing ? 3
    : hasResults ? (isPaymentComplete ? 5 : 4)
    : hasFiles ? 2
    : 1;
  const downloadsReady = isPaymentComplete && !unlockingPaidAssets && !results.some((r) => r.previewOnly && r.obscura_job_id);
  const editorCandidates = results
    .map((r, i) => ({ r, i }))
    .filter(({ r }) => r.obscura_job_id);
  const editorAvailable = downloadsReady && editorCandidates.length > 0;
  // Sopra i tre documenti la griglia con le anteprime grandi smette di
  // aiutare: dieci di quelle sono seimila pixel di scorrimento.
  const elencoCompatto = results.length > 3;

  const activeEditorRow = editorCandidates.find(({ i }) => i === editorDocIndex) || editorCandidates[0] || null;
  // Documenti con una revisione fatta nell'editor e non ancora mandata al
  // motore. Il documento aperto compare anche lui, se e' stato toccato.
  const docsModificati = editorCandidates.filter(({ i }) => statoEditor[i]?.modificato && statoEditor[i]?.bboxes?.length);
  // Lavorazione in corso degli altri documenti: { fatti, totale } o null.
  const [batchRedazione, setBatchRedazione] = useState(null);
  const aggiornaRigaRedatta = (rowIndex, bytes, filename) => {
    setResults((prev) => {
      const row = prev[rowIndex];
      if (!row) return prev;
      revokeResultPreviewUrls([row]);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const next = [...prev];
      next[rowIndex] = {
        ...row,
        redacted_filename: filename,
        blob,
        previewUrl: URL.createObjectURL(blob),
        content_base64: null,
        previewOnly: false,
      };
      return next;
    });
    // Le aree sono impresse nel documento: la revisione locale e' conclusa.
    setStatoEditor((prev) => ({ ...prev, [rowIndex]: null }));
  };
  const handleEditorRedactComplete = (rowIndex) => (bytes, filename, opzioni = {}) => {
    aggiornaRigaRedatta(rowIndex, bytes, filename);
    if (opzioni?.restaNellEditor) return;
    setResultsViewTab("preview");
    setStatusMessage("Documento aggiornato dall'editor.");
  };
  // "Anonimizza tutti": l'editor ha gia' lavorato il documento aperto e passa
  // qui la mano per gli altri, uno alla volta.
  const applicaAltriDocumenti = async (fattoQuelloAperto) => {
    const correnteIdx = activeEditorRow?.i;
    const voci = docsModificati
      .filter(({ i }) => i !== correnteIdx)
      .map(({ r, i }) => ({
        indice: i,
        nome: r.redacted_filename || `documento ${i + 1}`,
        jobId: r.obscura_job_id,
        fileIndex: fileIndexForResult(results, i),
        sessionId: abbonata ? null : stripeCheckoutSessionId,
        bboxes: statoEditor[i].bboxes,
        confermati: statoEditor[i].confermati,
      }));
    const totaleFatti = (esitoFatti) => esitoFatti + (fattoQuelloAperto ? 1 : 0);
    if (!voci.length) {
      setResultsViewTab("preview");
      setStatusMessage(fattoQuelloAperto ? "Documento aggiornato dall'editor." : "");
      return;
    }
    setError(null);
    setBatchRedazione({ fatti: 0, totale: voci.length });
    const esito = await applicaRedazioni(voci, {
      onAvanzamento: ({ fatti, totale }) => setBatchRedazione({ fatti, totale }),
    });
    for (const f of esito.fatti) aggiornaRigaRedatta(f.indice, f.bytes, f.filename);
    setBatchRedazione(null);
    if (esito.errori.length) {
      setError(`Anonimizzazione non riuscita su ${esito.errori.length === 1 ? "un documento" : `${esito.errori.length} documenti`}: ${esito.errori.map((e) => `${e.nome} (${e.messaggio})`).join("; ")}. Gli altri sono stati aggiornati.`);
    } else {
      setResultsViewTab("preview");
      const n = totaleFatti(esito.fatti.length);
      setStatusMessage(`${n} documenti aggiornati dall'editor.`);
    }
  };
  // Dopo lo sblocco dei documenti la pagina porta l'utente al riquadro di
  // scaricamento e lo mette in evidenza: chi ha appena pagato deve vedere
  // subito cosa fare, e sapere fino a quando puo' farlo.
  const scaricaBoxRef = useRef(null);
  const [evidenziaScarica, setEvidenziaScarica] = useState(false);
  const prontiPrimaRef = useRef(false);
  useEffect(() => {
    if (!downloadsReady || !results.length) {
      if (!downloadsReady) prontiPrimaRef.current = false;
      return undefined;
    }
    if (prontiPrimaRef.current) return undefined;
    prontiPrimaRef.current = true;
    if (!abbonata) {
      const entro = oraScadenza(leggiLavoro());
      const comeScaricare = results.length === 1 ? 'il documento con "Scarica"' : 'i documenti con "Scarica tutti"';
      setStatusMessage(`Pagamento confermato: ora scarica ${comeScaricare}${entro ? `. Restano disponibili fino alle ${entro}` : ""}.`);
    }
    setEvidenziaScarica(true);
    const timer = setTimeout(() => setEvidenziaScarica(false), 6000);
    const raf = requestAnimationFrame(() => {
      scaricaBoxRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    });
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [downloadsReady, results.length, abbonata]);
  // L'errore sotto la card (report, download) sta in fondo alla pagina: se
  // compare fuori dallo schermo, la pagina scorre quel tanto che basta.
  const erroreRisultatiRef = useRef(null);
  useEffect(() => {
    if (error && results.length) erroreRisultatiRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [error, results.length]);
  // I file rimasti fuori perche' troppo grandi: sopra l'area vuota o sotto
  // l'elenco, con la sua X per chiuderlo.
  const riquadroScartati = (stile) => fileScartati && (
    <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", borderRadius: 10, background: "#fff0f0", border: "1px solid #f5c6c6", fontSize: 12.5, color: "#c0392b", lineHeight: 1.5, boxSizing: "border-box", ...stile }}>
      <span style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>{fileScartati}</span>
      <button type="button" onClick={() => setFileScartati(null)} aria-label="Chiudi l'avviso" style={{ background: "none", border: "none", padding: 0, color: "#c0392b", cursor: "pointer", fontSize: 18, lineHeight: 1, flexShrink: 0 }}>&times;</button>
    </div>
  );
  const anonActionBtn = { height: 42, fontSize: 13, padding: "0 28px" };
  const anonUploadBtn = { height: 48, fontSize: 14, fontWeight: 500, padding: "0 36px" };
  const anonCompactBtn = { height: 34, fontSize: 11, padding: "0 16px" };

  return (
    <div style={{ minHeight: "100vh", background: C.smartBlue10, fontFamily: F.b, display: "flex", alignItems: "stretch" }}>
      {/* L'elaborazione non e' piu' un sipario a tutto schermo: era `position:
          fixed; inset: 0; zIndex: 200` e copriva anche la top bar, lasciando
          l'utente senza contesto e senza via d'uscita per tutta l'attesa.
          Ora e' uno stato dentro la pagina, sotto la top bar, che resta
          visibile. */}
      <style>{`@keyframes obscura-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}@keyframes obscura-progress{0%{width:10%;margin-left:0}50%{width:60%;margin-left:20%}100%{width:10%;margin-left:90%}}`}</style>

      {/* La barra laterale c'e' per tutti: chi non ha un account ci trova
          comunque i lavori dell'ultima ora di questo browser, da riaprire. */}
      <Sidebar
        isMobile={isMobile}
        aperta={sidebarAperta}
        onChiudi={() => setSidebarAperta(false)}
        onNuova={() => { setSidebarAperta(false); reset(); window.scrollTo(0, 0); }}
        onNavigateTo={(pagina, parametri) => { setSidebarAperta(false); onNavigateTo && onNavigateTo(pagina, parametri); }}
        onRiapri={riapriLavoro}
        jobIdsAperti={lavoroAperto.length ? lavoroAperto : [...new Set(results.map((r) => r.obscura_job_id).filter(Boolean))]}
      />

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
      {/* La top bar porta la barra dei passi al posto del titolo: e' lei a
          dire dove si e', e il titolo rubava solo spazio. */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: isMobile ? "0 12px" : "0 24px", height: ALTEZZA_BARRA, minHeight: ALTEZZA_BARRA, display: "flex", alignItems: "center", justifyContent: "space-between", gap: isMobile ? 8 : 16, position: "sticky", top: 0, zIndex: 50 }}>
        {/* Niente "Torna indietro" e niente "Accedi" qui: al sito si torna dal
            logo della barra laterale, e l'accesso sta in fondo alla barra.
            I due lati restano larghi uguale per tenere i passi al centro. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, minWidth: isMobile ? 36 : 120 }}>
          {isMobile && <BottoneMenu onClick={() => setSidebarAperta(true)} />}
        </div>
        <ProcessSteps step={passoCorrente} isMobile={isMobile} inline />
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, minWidth: isMobile ? 36 : 120, justifyContent: "flex-end" }}>
          {account && (
            <BottoneEsci onClick={async () => { await esciAccount(); onNavigateTo && onNavigateTo("landing"); }} />
          )}
        </div>
      </div>

      {isAnonymizing ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24, padding: isMobile ? "32px 16px 48px" : "48px 32px 64px" }}>
          <Rotellina />
          <p role="status" aria-live="polite" style={{ margin: 0, fontSize: 16, fontFamily: F.b, color: C.textTitle, textAlign: "center", lineHeight: 1.6 }}>
            {statusMessage || "Analisi in corso."}<br/><span style={{ color: C.textNum, fontSize: 14 }}>OBSCURA sta lavorando per te.</span>
          </p>
          <ProcessingCards isMobile={isMobile} />
        </div>
      ) : ripristinoInCorso ? (
        /* "Riapri", ritorno dal pagamento, link dell'email: il backend
           prepara le anteprime al momento, e intanto la pagina restava
           vuota sulla schermata di caricamento. */
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24, padding: isMobile ? "32px 16px 48px" : "48px 32px 64px" }}>
          <Rotellina />
          <p role="status" aria-live="polite" style={{ margin: 0, fontSize: 16, fontFamily: F.b, color: C.textTitle, textAlign: "center", lineHeight: 1.6 }}>
            Recupero dei documenti...<br/><span style={{ color: C.textNum, fontSize: 14 }}>Ci vuole qualche secondo.</span>
          </p>
        </div>
      ) : hasResults ? (
        <div style={{ padding: pagePadding }}>
          {editorAvailable && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", gap: 0, justifyContent: "center", borderBottom: `1px solid ${C.border}`, marginBottom: 12 }}>
                {[
                  { id: "preview", label: "Anteprima" },
                  { id: "editor", label: "Editor" },
                ].map((tab) => {
                  const active = resultsViewTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setResultsViewTab(tab.id)}
                      style={{
                        padding: "10px 20px",
                        fontSize: 13,
                        fontWeight: 600,
                        fontFamily: F.b,
                        border: "none",
                        borderBottom: active ? `2px solid ${C.smartBlue}` : "2px solid transparent",
                        background: "transparent",
                        color: active ? C.smartBlue : C.textNum,
                        cursor: "pointer",
                        marginBottom: -1,
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {resultsViewTab === "preview" && (
                <p style={{ fontSize: 12, color: C.textNum, margin: 0, lineHeight: 1.45, textAlign: "center" }}>
                  Vuoi perfezionare le aree da anonimizzare? Apri l&apos;<button type="button" onClick={() => setResultsViewTab("editor")} style={{ background: "none", border: "none", padding: 0, color: C.smartBlue, fontWeight: 600, cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>Editor</button> sul PDF originale con i riquadri suggeriti dal modello.
                </p>
              )}
            </div>
          )}

          {resultsViewTab === "editor" && editorAvailable && activeEditorRow ? (
            <div style={{ background: C.white, borderRadius: C.radiusSection, border: `1px solid ${C.border}`, padding: isMobile ? 16 : 24, marginBottom: 24 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                {editorCandidates.map(({ r, i }) => {
                  const active = editorDocIndex === i;
                  const label = r.redacted_filename || `Documento ${i + 1}`;
                  // Numero esatto solo per i documenti gia' aperti: prima si
                  // sa soltanto che ci sono segnalazioni, non quante nella
                  // stessa unita' di misura dell'editor.
                  const esatto = sospettiPerDoc[i];
                  const daVedere = esatto != null ? esatto > 0 : (r.sospetti || 0) > 0;
                  const tagStyle = {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: `1px solid ${active ? C.smartBlue : C.border}`,
                    background: active ? C.smartBlue20 : C.white,
                    color: active ? C.smartBlue : C.textNum,
                    fontSize: 11,
                    fontWeight: 600,
                  };
                  const modificato = Boolean(statoEditor[i]?.modificato && statoEditor[i]?.bboxes?.length);
                  const segnoModifica = modificato ? (
                    <span
                      title="Modifiche non ancora applicate"
                      style={{ width: 8, height: 8, borderRadius: "50%", background: C.smartBlue, flexShrink: 0 }}
                    />
                  ) : null;
                  const segno = !daVedere ? null : esatto != null ? (
                    <span style={{ background: "#D97706", color: "#fff", borderRadius: 999, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>
                      {esatto}
                    </span>
                  ) : (
                    <span
                      title="Contiene aree da controllare"
                      style={{ width: 8, height: 8, borderRadius: "50%", background: "#D97706", flexShrink: 0 }}
                    />
                  );
                  if (editorCandidates.length === 1) {
                    return <span key={i} style={tagStyle}>{label}{segno}{segnoModifica}</span>;
                  }
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setEditorDocIndex(i)}
                      style={{ ...tagStyle, cursor: "pointer" }}
                    >
                      {label}{segno}{segnoModifica}
                    </button>
                  );
                })}
              </div>
              {editorCandidates.length > 1 && (() => {
                const daControllare = editorCandidates.filter(({ r, i }) => {
                  const esatto = sospettiPerDoc[i];
                  return esatto != null ? esatto > 0 : (r.sospetti || 0) > 0;
                }).length;
                if (!daControllare) return null;
                return (
                  <p style={{ margin: "0 0 16px", fontSize: 12, color: C.textNum, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#D97706", flexShrink: 0 }} />
                    {daControllare} {daControllare === 1 ? "documento contiene aree" : "documenti contengono aree"} da controllare
                  </p>
                );
              })()}
              {docsModificati.length > 0 && editorCandidates.length > 1 && !batchRedazione && (
                <p style={{ margin: "0 0 16px", fontSize: 12, color: C.textNum, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.smartBlue, flexShrink: 0 }} />
                  {docsModificati.length === 1 ? "1 documento ha" : `${docsModificati.length} documenti hanno`} modifiche non ancora applicate
                </p>
              )}
              {batchRedazione && (
                <div role="status" style={{ margin: "0 0 16px", padding: "10px 14px", borderRadius: 10, background: C.smartBlue10, border: `1px solid ${C.border}`, fontSize: 13, color: C.textBody }}>
                  Anonimizzazione degli altri documenti: {Math.min(batchRedazione.fatti + 1, batchRedazione.totale)} di {batchRedazione.totale}...
                </div>
              )}
              <PdfEditor
                key={`${activeEditorRow.r.obscura_job_id}-${fileIndexForResult(results, activeEditorRow.i)}`}
                jobId={activeEditorRow.r.obscura_job_id}
                fileIndex={fileIndexForResult(results, activeEditorRow.i)}
                sessionId={abbonata ? null : stripeCheckoutSessionId}
                onRedactComplete={handleEditorRedactComplete(activeEditorRow.i)}
                onSospettiChange={(n) => registraSospetti(activeEditorRow.i, n)}
                statoSalvato={statoEditor[activeEditorRow.i]}
                onStatoChange={(st) => registraStatoEditor(activeEditorRow.i, st)}
                altriDaApplicare={docsModificati.filter(({ i }) => i !== activeEditorRow.i).length}
                onRedactAll={applicaAltriDocumenti}
                bloccato={!!batchRedazione}
              />
            </div>
          ) : (
          elencoCompatto ? (
          /* Sopra i tre file la griglia smette di aiutare: dieci anteprime da
             600px sono seimila pixel di scorrimento e dieci PDF renderizzati
             insieme. Qui l'elenco e' compatto e l'anteprima si apre su
             richiesta, una alla volta. */
          <div style={{ background: C.white, borderRadius: C.radiusSection, border: `1px solid ${C.border}`, overflow: "hidden", marginBottom: 24 }}>
            {results.map((r, i) => {
              const aperta = anteprimaAperta === i;
              const esatto = sospettiPerDoc[i];
              const daVedere = esatto != null ? esatto > 0 : (r.sospetti || 0) > 0;
              return (
                <div key={i} style={{ borderTop: i === 0 ? "none" : `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: isMobile ? "12px 14px" : "14px 20px", flexWrap: "wrap" }}>
                    <span style={{ flexShrink: 0, color: C.smartBlue, display: "inline-flex" }}>{icons.file}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.textTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.redacted_filename || `Documento ${i + 1}`}
                      </p>
                      <p style={{ margin: 0, fontSize: 11, color: C.textNum, display: "flex", alignItems: "center", gap: 8 }}>
                        {pagesForResult(r, i)} {pagesForResult(r, i) === 1 ? "pagina" : "pagine"}
                        {daVedere && (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#9A3412", fontWeight: 700 }}>
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#D97706" }} />
                            {esatto != null ? `${esatto} da controllare` : "da controllare"}
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAnteprimaAperta(aperta ? null : i)}
                      style={{ height: 30, padding: "0 12px", borderRadius: 6, border: `1px solid ${C.border}`, background: C.white, color: C.textTitle, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: F.b }}
                    >
                      {aperta ? "Chiudi anteprima" : "Anteprima"}
                    </button>
                    <GBtn variant="outline" onClick={() => downloadResult(r)} style={{ ...anonCompactBtn, opacity: downloadsReady ? 1 : 0.6, pointerEvents: downloadsReady ? "auto" : "none" }}>Scarica</GBtn>
                  </div>
                  {aperta && (
                    <div style={{ position: "relative", background: "#000", height: isMobile ? 420 : 600, width: "100%", overflow: "hidden" }}>
                      <DocumentResultPreview result={r} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          ) : (
          <div style={{ display: "grid", gridTemplateColumns: results.length === 1 || isMobile ? "1fr" : "repeat(auto-fit, minmax(320px, 1fr))", gap: 24, marginBottom: 24 }}>
            {results.map((r, i) => <div key={i} style={{ background: C.white, borderRadius: C.radiusSection, border: `1px solid ${C.border}`, overflow: "hidden", minWidth: 0 }}>
              {/* Il nome lungo si accorcia con i puntini: prima spingeva il
                  tasto fuori dalla scheda sui telefoni. Il tasto per documento
                  c'e' solo con piu' documenti: con uno solo fa quello che fa
                  "Scarica" qui sotto. */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "16px 20px" }}><div style={{ minWidth: 0, flex: "1 1 auto" }}><h3 style={{ fontSize: 15, fontWeight: 600, fontFamily: F.h, color: C.textTitle }}>Documento {i+1}</h3><p title={r.redacted_filename} style={{ fontSize: 12, color: C.textNum, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.redacted_filename}</p></div>{results.length > 1 && <GBtn variant="outline" onClick={() => downloadResult(r)} style={{ ...anonCompactBtn, flexShrink: 0, opacity: downloadsReady ? 1 : 0.6, pointerEvents: downloadsReady ? "auto" : "none" }}>Scarica</GBtn>}</div>
              <div style={{ position: "relative", background: "#000", height: isMobile ? 420 : 600, width: "100%", overflow: "hidden" }}><DocumentResultPreview result={r} /></div>
              <div style={{ display: "flex", justifyContent: "center", gap: 12, padding: "12px 0" }}><span style={{ fontSize: 13, color: C.textNum }}>{pagesForResult(r, i)} {pagesForResult(r, i) === 1 ? "pagina" : "pagine"}</span></div>
            </div>)}
          </div>
          )
          )}
          {(resultsViewTab === "preview" || !editorAvailable) && (
          <>
          <div ref={scaricaBoxRef} style={{ background: C.white, borderRadius: C.radiusCard, padding: isMobile ? "16px" : "16px 24px", display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", border: `1px solid ${evidenziaScarica ? C.smartBlue : C.border}`, boxShadow: evidenziaScarica ? `0 0 0 4px ${C.smartBlue30}` : "none", transition: "box-shadow .4s ease, border-color .4s ease", gap: 20 }}>
            {/* Il testo va a capo prima dei pulsanti: con tre azioni (il
                report per gli abbonati) restano tutte su una riga. */}
            <div style={{ flex: isMobile ? "none" : "1 1 220px", minWidth: 0 }}>
              {/* Pagati (o compresi nel piano) i documenti non sono piu' da
                  acquistare: via il prezzo, e "Nuova anonimizzazione" entra
                  nella card, prima di "Scarica" ("Scarica tutti" con piu'
                  documenti). */}
              <span style={{ fontSize: 14, fontWeight: 600, color: C.textTitle, fontFamily: F.h, fontStyle: "italic", display: "block" }}>{isPaymentComplete ? "Documenti pronti per il download" : "Documenti pronti per l'acquisto"}</span>
              {abbonata ? (
                <span style={{ fontSize: 12, color: C.textNum }}>
                  {totalPages} {totalPages === 1 ? "pagina" : "pagine"}, comprese nel piano {account.azienda.piano_nome}
                  {account.consumo && account.consumo.pagine_incluse != null && <> · {account.consumo.pagine_rimanenti} pagine incluse rimanenti questo mese</>}
                </span>
              ) : !isPaymentComplete ? (
                <span style={{ fontSize: 12, color: C.textNum }}>{totalPages} {totalPages === 1 ? "pagina" : "pagine"} - Totale {moneyFmt.format(totalCost)} (minimo {moneyFmt.format(MIN_CHARGE_EUR)})</span>
              ) : null}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", flex: isMobile ? "none" : "0 1 auto", justifyContent: isMobile ? "flex-start" : "flex-end" }}>
              {!isPaymentComplete && <GBtn variant="blue" onClick={startCheckout} style={{ ...anonActionBtn, opacity: isPaying ? 0.7 : 1 }}>{isPaying ? "Apertura checkout..." : "Procedi all'acquisto"}</GBtn>}
              {isPaymentComplete && <GBtn variant="outline" onClick={reset} style={anonActionBtn}>← Nuova anonimizzazione</GBtn>}
              {isPaymentComplete && reportDisponibile && (
                <GBtn variant="outline" onClick={scaricaReport} style={{ ...anonActionBtn, opacity: downloadsReady && !preparaReport ? 1 : 0.6, pointerEvents: downloadsReady && !preparaReport ? "auto" : "none" }}>
                  {preparaReport ? "Preparazione del report..." : "Report di conformità"}
                </GBtn>
              )}
              <GBtn variant="primary" onClick={downloadAll} style={{ ...anonActionBtn, opacity: downloadsReady ? 1 : 0.6, pointerEvents: downloadsReady ? "auto" : "none" }}>{icons.doc} {results.length === 1 ? "Scarica" : "Scarica tutti"}</GBtn>
            </div>
          </div>
          {/* Gli errori di download e del report: senza questo riquadro, con
              i risultati a schermo non comparivano da nessuna parte. */}
          {error && <div ref={erroreRisultatiRef} role="alert" style={{ marginTop: 12, padding: "12px 16px", borderRadius: 10, background: "#fff0f0", border: "1px solid #f5c6c6", fontSize: 13, color: "#c0392b", lineHeight: 1.5 }}>{error}</div>}
          {isPaymentComplete && unlockingPaidAssets && <div style={{ marginTop: 12, fontSize: 12, color: C.textNum }}>Sto recuperando la versione completa dei documenti (anteprima sfocata sostituita)...</div>}
          {isPaymentComplete && results.some((row) => row.previewOnly && row.obscura_job_id) && !stripeCheckoutSessionId && !unlockingPaidAssets && (
            <div style={{ marginTop: 12, fontSize: 12, color: C.textBody, lineHeight: 1.45 }}>
              Manca il riferimento alla sessione di pagamento Stripe. Completa il ritorno dal checkout (link &quot;Torna indietro&quot;) oppure ricarica la pagina dopo il pagamento.
            </div>
          )}
          {checkoutStarted && !isPaymentComplete && <div style={{ marginTop: 12, fontSize: 12, color: C.textNum }}>Completa il pagamento nel popup Stripe: questa pagina si aggiorna automaticamente e sblocca i download.</div>}
          {!abbonata && !isPaymentComplete && <StripeTrustLine style={{ marginTop: 14, textAlign: isMobile ? "left" : "center" }} />}
          </>
          )}
          {/* Qui sotto resta prima dell'acquisto, e quando l'editor nasconde la card. */}
          {(!isPaymentComplete || !(resultsViewTab === "preview" || !editorAvailable)) && (
            <div style={{ textAlign: "center", marginTop: 24 }}><GBtn variant="outline" onClick={reset} style={anonActionBtn}>← Nuova anonimizzazione</GBtn></div>
          )}
        </div>
      ) : !hasFiles ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: `calc(100vh - ${ALTEZZA_BARRA}px)`, padding: isMobile ? 16 : 40 }} onDrop={handleDrop} onDragOver={(e) => { e.preventDefault(); if (privacyAccepted) setDragging(true); }} onDragLeave={() => setDragging(false)}>
          {/* L'errore qui: prima stava solo nel pannello delle opzioni, che
              senza file non c'e', e un lavoro riaperto ma gia' scaduto (dal
              link dell'email o da "Riapri") lasciava la pagina muta. */}
          {error && <div role="alert" style={{ maxWidth: 700, width: "100%", boxSizing: "border-box", marginBottom: 16, padding: "12px 16px", borderRadius: 10, background: "#fff0f0", border: "1px solid #f5c6c6", fontSize: 13, color: "#c0392b", lineHeight: 1.5 }}>{error}</div>}
          {riquadroScartati({ maxWidth: 700, width: "100%", marginBottom: 16, padding: "12px 16px", fontSize: 13 })}
          <div style={{ background: C.white, borderRadius: C.radiusSection, padding: isMobile ? "36px 20px" : "80px 60px", textAlign: "center", maxWidth: 700, width: "100%", boxShadow: C.shadow, border: dragging ? `2px dashed ${C.smartBlue}` : `1px solid ${C.border}` }}>
            <div style={{ width: 72, height: 72, borderRadius: 16, background: C.smartBlue20, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>{icons.uploadCloud}</div>
            <h2 style={{ fontSize: isMobile ? 28 : 36, fontWeight: 400, fontFamily: F.h, color: C.textTitle, marginBottom: 16 }}>Carica file o cartelle</h2>
            <p style={{ fontSize: 14, fontWeight: 500, color: C.textNum, lineHeight: "22px", marginBottom: 24 }}>Trascina i file in quest'area oppure usa il pulsante qui sotto.<br/>Formati supportati: .pdf, .docx, .csv, .pptx, .png, .jpg, .jpeg <br/> Caricamento massimo: {testoLimiti(limiti)}</p>
            {/* Un pulsante solo: i due di prima chiamavano lo stesso
                fileInputRef.current.click(), e quello chiamato "Trascina file
                qui" apriva il selettore invece di trascinare. Il trascinamento
                vero e' sul contenitore esterno (onDrop), e funziona da sempre. */}
            <div style={{ display: "flex", justifyContent: "center", width: "100%", maxWidth: 320, margin: "0 auto" }}>
              <GBtn variant="blue" disableMagnet style={{ ...anonUploadBtn, justifyContent: "center", width: "100%", boxSizing: "border-box", lineHeight: 1, opacity: privacyAccepted ? 1 : 0.5, pointerEvents: privacyAccepted ? "auto" : "none", cursor: privacyAccepted ? "pointer" : "not-allowed" }} onClick={() => { if (privacyAccepted) fileInputRef.current?.click(); }}>{icons.upload} Carica i tuoi file</GBtn>
            </div>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, textAlign: "left", maxWidth: 480, margin: "24px auto 0", padding: isMobile ? "14px 16px" : "16px 20px", borderRadius: C.radiusCard, background: C.smartWhite, border: `1px solid ${privacyAccepted ? C.smartBlue30 : C.border}`, cursor: "pointer" }}>
              <input type="checkbox" checked={privacyAccepted} onChange={() => setPrivacyAccepted(p => !p)} style={{ width: 16, height: 16, accentColor: C.smartBlue, marginTop: 2, flexShrink: 0 }}/>
              <span style={{ fontSize: 12, color: C.textBody, lineHeight: 1.5 }}>
                È necessario accettare l'<a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.smartBlue, textDecoration: "underline", fontWeight: 600 }}>informativa privacy</a> prima di caricare i file
              </span>
            </label>
          </div>
          {/* Il valore si azzera dopo ogni scelta: altrimenti scegliere di
              nuovo lo stesso file (tolto o rifiutato) non fa succedere niente. */}
          <input ref={fileInputRef} type="file" multiple hidden onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} accept=".pdf,.docx,.csv,.pptx,.png,.jpg,.jpeg,image/png,image/jpeg"/>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1fr", gap: 24, padding: pagePadding }} onDrop={handleDrop} onDragOver={e => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}>
          <div style={{ minWidth: 0, background: C.white, borderRadius: C.radiusSection, padding: isMobile ? "20px" : "28px", border: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, marginBottom: 20 }}>
              {/* Stessa duplicazione dell'area di caricamento, stessa cura:
                  i due pulsanti chiamavano lo stesso selettore di file. */}
              <GBtn variant="blue" disableMagnet style={{ flex: isMobile ? "0 0 auto" : 1, width: isMobile ? "100%" : undefined, boxSizing: "border-box", justifyContent: "center", ...anonActionBtn }} onClick={() => fileInputRef.current?.click()}>{icons.upload} Aggiungi altri file</GBtn>
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div><h4 style={{ fontSize: 16, fontWeight: 600, fontFamily: F.h, color: C.textTitle }}>Documenti Caricati</h4><p style={{ fontSize: 12, color: C.textNum }}>{files.length} file pronti</p></div>
                <button onClick={removeAll} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, color: C.smartBlue, fontFamily: F.b }}>{icons.trash} Rimuovi tutto</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
                {files.map((f, i) => { const m = miniature.get(f); return <div key={i} style={{ background: C.smartWhite, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px", position: "relative" }}>
                  {/* La miniatura: prima pagina del PDF o l'immagine; per
                      DOCX, PPTX e CSV il tipo del file, perche' nel browser
                      non c'e' niente che li disegni. */}
                  <div style={{ width: "100%", height: 104, background: C.smartBlue20, border: `1px solid ${C.border}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 8, overflow: "hidden" }}>
                    {m?.url ? (
                      <img src={m.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block", background: "#fff" }} />
                    ) : (
                      <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                        {icons.file}
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: C.smartBlue, fontFamily: F.b }}>{tipoFile(f.name).toUpperCase()}</span>
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 11, fontWeight: 600, color: C.textTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</p>
                  <p style={{ fontSize: 10, color: C.textNum }}>{(f.size/1024).toFixed(1)} KB · {pagesByFile.has(f) ? `${pagesByFile.get(f)} ${pagesByFile.get(f) === 1 ? "pagina" : "pagine"}` : (isCountingPages ? "conteggio..." : "—")}</p>
                  <button onClick={() => removeFile(i)} style={{ position: "absolute", top: 6, right: 6, background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><Ico size={10} color={C.textNum}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico></button>
                </div>; })}
              </div>
              {avvisoLimiti && <div role="alert" style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, background: "#fff0f0", border: "1px solid #f5c6c6", fontSize: 12.5, color: "#c0392b", lineHeight: 1.5 }}>{avvisoLimiti}</div>}
              {riquadroScartati({ marginTop: 14 })}
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ background: C.white, borderRadius: C.radiusSection, padding: isMobile ? "20px" : "28px", border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "space-between", marginBottom: 16 }}>
                <div><h3 style={{ fontSize: 16, fontWeight: 600, fontFamily: F.h, color: C.textTitle, fontStyle: "italic" }}>Categorie da Anonimizzare</h3><p style={{ fontSize: 12, color: C.textNum }}>Seleziona le tipologie di dati sensibili</p></div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textNum, cursor: "pointer" }}>Seleziona tutto <input type="checkbox" checked={selectAll} onChange={toggleSelectAll} style={{ width: 16, height: 16, accentColor: C.smartBlue }}/></label>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {CATEGORIES.map(cat => { const sel = countSelected(cat); const isExp = expanded[cat.name]; return <div key={cat.name} style={{ border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
                  <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", cursor: "pointer", gap: 12 }} onClick={() => setExpanded(p => ({ ...p, [cat.name]: !p[cat.name] }))}>
                    <input type="checkbox" checked={sel === cat.count} onChange={() => toggleCat(cat.name)} onClick={e => e.stopPropagation()} style={{ width: 16, height: 16, accentColor: C.smartBlue }}/>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.textTitle, flex: 1 }}>{cat.name}</span>
                    <span style={{ fontSize: 12, color: C.textNum }}>{sel} di {cat.count}</span>
                    <span style={{ transform: isExp ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>{icons.chevron}</span>
                  </div>
                  {isExp && <div style={{ padding: "0 16px 12px", display: "flex", flexWrap: "wrap", gap: 6 }}>{cat.items.map(it => <label key={it} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: selectedCats[it] ? C.smartBlue : C.textNum, background: selectedCats[it] ? C.smartBlue20 : C.smartWhite, border: `1px solid ${selectedCats[it] ? C.smartBlue30 : C.border}`, borderRadius: 8, padding: "5px 9px", cursor: "pointer", fontWeight: 500 }}><input type="checkbox" checked={!!selectedCats[it]} onChange={() => toggleItem(it)} style={{ width: 13, height: 13, accentColor: C.smartBlue }}/>{it}</label>)}</div>}
                </div>; })}
              </div>
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={blurFaces} onChange={() => setBlurFaces(p => !p)} style={{ width: 16, height: 16, accentColor: C.smartBlue, marginTop: 2, flexShrink: 0 }}/>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 600, fontFamily: F.h, color: C.textTitle }}>Sfoca i volti</span>
                    <p style={{ fontSize: 12, color: C.textNum, margin: "4px 0 0", lineHeight: 1.5 }}>Rileva e sfoca i volti presenti in immagini e documenti con foto, oltre alle categorie selezionate.</p>
                  </div>
                </label>
              </div>
              {funzioni.includes("pseudonimizzazione") && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                    <input type="checkbox" checked={pseudonimizza} onChange={() => setPseudonimizza(p => !p)} style={{ width: 16, height: 16, accentColor: C.smartBlue, marginTop: 2, flexShrink: 0 }}/>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 600, fontFamily: F.h, color: C.textTitle }}>Anonimizza con placeholder</span>
                      <p style={{ fontSize: 12, color: C.textNum, margin: "4px 0 0", lineHeight: 1.5 }}>Al posto della barra nera, ogni dato diventa un segnaposto come [PERSONA_1]: lo stesso valore riceve sempre lo stesso segnaposto, cos&igrave; il documento resta leggibile.</p>
                    </div>
                  </label>
                </div>
              )}
              {funzioni.includes("firme") && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                    <input type="checkbox" checked={sfocaFirme} onChange={() => setSfocaFirme(p => !p)} style={{ width: 16, height: 16, accentColor: C.smartBlue, marginTop: 2, flexShrink: 0 }}/>
                    <div>
                      <span style={{ fontSize: 14, fontWeight: 600, fontFamily: F.h, color: C.textTitle }}>Sfoca le firme</span>
                      <p style={{ fontSize: 12, color: C.textNum, margin: "4px 0 0", lineHeight: 1.5 }}>Rileva le firme autografe e le sfoca, con un secondo controllo che scarta i falsi positivi.</p>
                    </div>
                  </label>
                </div>
              )}
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
                <h4 style={{ fontSize: 14, fontWeight: 600, fontFamily: F.h, color: C.textTitle }}>Parole specifiche da anonimizzare</h4>
                <p style={{ fontSize: 12, color: C.textNum, marginBottom: 10 }}>Inserisci parole (una per riga o separate da virgola). Verranno anonimizzate indipendentemente dalle categorie selezionate.</p>
                <textarea
                  value={wordsToRedact}
                  onChange={e => setWordsToRedact(e.target.value)}
                  placeholder={"3/0726\nAvv.\nn.005"}
                  rows={3}
                  style={{ width: "100%", boxSizing: "border-box", resize: "vertical", borderRadius: 10, border: `1px solid ${C.border}`, padding: "10px 12px", fontSize: 13, fontFamily: F.b, color: C.textBody, background: C.smartWhite, outline: "none" }}
                />
                {getWordsToRedact().length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                    {getWordsToRedact().map(w => <span key={w} style={{ display: "inline-flex", alignItems: "center", fontSize: 11, fontWeight: 500, color: C.smartBlue, background: C.smartBlue20, border: `1px solid ${C.smartBlue30}`, borderRadius: 8, padding: "4px 9px" }}>{w}</span>)}
                  </div>
                )}
                {paroleSalvateAttive && (
                  <UsaParoleSalvate
                    voci={paroleSalvate}
                    usa={usaParoleSalvate}
                    onUsa={setUsaParoleSalvate}
                    onGestisci={() => onNavigateTo && onNavigateTo("account")}
                  />
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 12, marginTop: 20 }}>
              <GBtn variant="outline" onClick={reset} style={{ ...anonActionBtn, justifyContent: "center", flex: isMobile ? "1 1 100%" : "0 0 auto" }}>{icons.reset} Reset</GBtn>
              <GBtn variant="primary" onClick={startAnonymization} style={{ ...anonActionBtn, justifyContent: "center", flex: isMobile ? "1 1 100%" : "0 0 auto", opacity: isAnonymizing ? 0.7 : 1 }}>{icons.shield} Anonimizza file</GBtn>
            </div>
            {statusMessage && !isAnonymizing && <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 10, background: C.smartBlue10, border: `1px solid ${C.border}`, fontSize: 12, color: C.textNum }}>{statusMessage}</div>}
            {error && <div style={{ marginTop: 16, padding: "12px 16px", borderRadius: 10, background: "#fff0f0", border: "1px solid #f5c6c6", fontSize: 13, color: "#c0392b" }}>{error}</div>}
          </div>
          {/* Il valore si azzera dopo ogni scelta: altrimenti scegliere di
              nuovo lo stesso file (tolto o rifiutato) non fa succedere niente. */}
          <input ref={fileInputRef} type="file" multiple hidden onChange={e => { handleFiles(e.target.files); e.target.value = ""; }} accept=".pdf,.docx,.csv,.pptx,.png,.jpg,.jpeg,image/png,image/jpeg"/>
        </div>
      )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   LANDING PAGE
   ═══════════════════════════════════════════ */
function ProductMark({ compact = false, nav = false, navWidth, mono = false, scale = 1 }) {
  const width = (navWidth != null ? navWidth : nav ? (compact ? 112 : 132) : compact ? 142 : 210) * scale;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: nav ? 6 : compact ? 8 : 12 }}>
      <img src={OBSCURA_LOGO} alt="OBSCURA powered by GRID+" style={{ width, height: "auto", display: "block", filter: mono ? "invert(1) saturate(0) brightness(1.85)" : "none", mixBlendMode: mono ? "screen" : "normal" }} />
    </div>
  );
}

function PremiumHero({ onNavigate, isMobile, isTablet, pageWrap }) {
  const heroPairStyle = {
    width: isMobile ? "100%" : "auto",
    maxWidth: isMobile ? "100%" : undefined,
    boxSizing: isMobile ? "border-box" : undefined,
    justifyContent: "center",
    height: isMobile ? 54 : 60,
    borderRadius: 14,
    fontSize: isMobile ? 14 : 15,
    padding: isMobile ? "0 22px" : "0 44px",
  };

  return (
    <HeroAurora
      isMobile={isMobile}
      isTablet={isTablet}
      pageWrap={pageWrap}
      sapienzaLogo={SAPIENZA_LOGO_WHITE}
      gridPlusLogo={GRID_PLUS_LOGO_WHITE}
      actions={(
        <>
          <GBtn variant="primaryLight" size="lg" onClick={onNavigate} iconRight={!isMobile} style={heroPairStyle}>Anonimizza un documento</GBtn>
          <GBtn variant="ghostLight" size="lg" href="#contatti" iconRight={false} style={heroPairStyle}>Contattaci</GBtn>
        </>
      )}
    />
  );
}

const PII_MARQUEE_ROW_A = PII_MARQUEE_ITEMS.filter((_, i) => i % 2 === 0);
const PII_MARQUEE_ROW_B = PII_MARQUEE_ITEMS.filter((_, i) => i % 2 === 1);

function PiiMarqueePill({ item, highlight = false }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 18px",
        borderRadius: 999,
        ...type.label,
        fontSize: 12,
        fontWeight: 700,
        color: C.textBody,
        whiteSpace: "nowrap",
        border: `1px solid ${C.border}`,
        background: highlight ? C.smartBlue10 : C.white,
        flexShrink: 0,
      }}
    >
      <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18 }} aria-hidden="true">
        {item.icon}
      </span>
      {item.label}
    </span>
  );
}

function PiiMarqueeRow({ items, direction = "ltr", duration = 140 }) {
  const repeat = [...items, ...items];
  const trackClass = direction === "ltr" ? "obscura-marquee-track-ltr" : "obscura-marquee-track-rtl";

  return (
    <div
      aria-hidden="true"
      style={{
        overflow: "hidden",
        width: "100%",
        WebkitMaskImage: "linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)",
        maskImage: "linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)",
      }}
    >
      <div
        className={trackClass}
        style={{
          display: "flex",
          gap: 12,
          width: "max-content",
          animation: `${direction === "ltr" ? "ob-marquee-ltr" : "ob-marquee-rtl"} ${duration}s linear infinite`,
          willChange: "transform",
        }}
      >
        {repeat.map((item, i) => (
          <PiiMarqueePill key={`${item.code}-${i}`} item={item} highlight={i % 5 === 0} />
        ))}
      </div>
    </div>
  );
}

function PiiMarquee({ isMobile, isTablet, pageWrap }) {
  return (
    <section style={{ padding: isMobile ? "50px 0 36px" : "70px 0 52px", position: "relative", overflowX: "hidden", background: C.sectionAlt }}>
      <div style={pageWrap}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 40 : 52, maxWidth: 920, marginLeft: "auto", marginRight: "auto" }}>
          <Reveal>
            <STitle style={{ margin: 0, letterSpacing: "-.02em" }}>
              58 categorie di dati sensibili, specifiche per la lingua italiana
            </STitle>
          </Reveal>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 20 : 28 }}>
        <PiiMarqueeRow items={PII_MARQUEE_ROW_A} direction="ltr" duration={isMobile ? 120 : 140} />
        <PiiMarqueeRow items={PII_MARQUEE_ROW_B} direction="rtl" duration={isMobile ? 150 : 165} />
      </div>
    </section>
  );
}

function AuroraDark({ intensity = 1 }) {
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", opacity: intensity }}>
      <div style={{ position: "absolute", width: "70vw", height: "70vw", left: "-20vw", top: "-25vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(0,156,221,.42), transparent 65%)", filter: "blur(40px)", animation: "ob-aurora-1 24s ease-in-out infinite" }} />
      <div style={{ position: "absolute", width: "60vw", height: "60vw", right: "-20vw", top: "20vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(89,201,244,.30), transparent 60%)", filter: "blur(50px)", animation: "ob-aurora-2 30s ease-in-out infinite" }} />
      <div style={{ position: "absolute", width: "50vw", height: "50vw", left: "40vw", bottom: "-15vw", borderRadius: "50%", background: "radial-gradient(circle, rgba(77,186,231,.20), transparent 60%)", filter: "blur(60px)", animation: "ob-aurora-3 28s ease-in-out infinite" }} />
    </div>
  );
}

function GridOverlay({ color = "rgba(255,255,255,.06)", size = 70, opacity = 0.5 }) {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        backgroundImage: `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`,
        backgroundSize: `${size}px ${size}px`,
        opacity,
        animation: "ob-grid-drift 30s linear infinite",
        WebkitMaskImage: "radial-gradient(ellipse at center, #000 50%, transparent 100%)",
        maskImage: "radial-gradient(ellipse at center, #000 50%, transparent 100%)",
      }}
    />
  );
}

const WorkflowIco = ({ children, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const WORKFLOW_ICN = {
  upload: <WorkflowIco><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></WorkflowIco>,
  shield: <WorkflowIco><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></WorkflowIco>,
  edit: <WorkflowIco><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></WorkflowIco>,
  download: <WorkflowIco><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></WorkflowIco>,
};

const WORKFLOW_STEPS = [
  { t: "Carica", d: "Carica uno o più file (PDF, DOCX, CSV, PPTX, PNG, JPG), senza alcun bisogno di registrarsi sulla piattaforma.", icon: WORKFLOW_ICN.upload },
  { t: "Anonimizza", d: "OBSCURA rileva e rimuove 58 categorie di dati sensibili, come nomi, IBAN, codice fiscale, indirizzi e altro. Il contenuto utile del documento resta intatto.", icon: WORKFLOW_ICN.shield },
  { t: "Perfeziona", d: "Apri l'editor integrato per rivedere e modificare le aree da anonimizzare: aggiungi, rimuovi o regola i riquadri suggeriti dal modello prima del download.", icon: WORKFLOW_ICN.edit },
  { t: "Scarica", d: "Ottieni il documento anonimizzato, pronto per analisi dati, RAG, condivisione interna o qualsiasi tipo di workflow AI.", icon: WORKFLOW_ICN.download },
];

function WorkflowHorizon({ isMobile, isTablet, pageWrap }) {
  return (
    <section
      id="come-funziona"
      style={{
        padding: isMobile ? "70px 0" : "120px 0",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(180deg, #061427 0%, #102845 100%)",
        color: "#fff",
        zIndex: 1,
      }}
    >
      <AuroraDark intensity={0.6} />
      <GridOverlay color="rgba(255,255,255,.06)" size={70} />

      <div style={{ ...pageWrap, position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "center", maxWidth: 820, margin: "0 auto 60px" }}>
          <Reveal>
            <STitle light style={{ marginTop: 0, marginBottom: 0, letterSpacing: "-.02em" }}>
              Rendi sicuri i tuoi documenti in 4 semplici passaggi.
            </STitle>
          </Reveal>
        </div>

        <div style={{ position: "relative" }}>
          {!isMobile && (
            <div
              aria-hidden
              style={{
                position: "absolute",
                left: "8%",
                right: "8%",
                top: 42,
                height: 1,
                background: `linear-gradient(90deg, transparent, ${C.blueAccent} 20%, ${C.blueAccent} 80%, transparent)`,
                opacity: 0.55,
              }}
            />
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
              gap: isMobile ? 24 : 18,
              position: "relative",
            }}
          >
            {WORKFLOW_STEPS.map((step, i) => (
              <Reveal key={step.t} delay={i * 80}>
                <div style={{ padding: "0 8px", textAlign: "center" }}>
                  <div
                    style={{
                      position: "relative",
                      width: 84,
                      height: 84,
                      borderRadius: "50%",
                      background: "radial-gradient(circle at 30% 30%, #0c2244, #051226)",
                      border: "1px solid rgba(89,201,244,.4)",
                      color: C.blueAccent,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 24px",
                      boxShadow: "0 0 0 6px rgba(89,201,244,.06), 0 12px 30px rgba(0,0,0,.4)",
                    }}
                  >
                    <span style={{ transform: "scale(1.4)" }}>{step.icon}</span>
                    <span
                      style={{
                        position: "absolute",
                        inset: -3,
                        borderRadius: "50%",
                        border: "1px dashed rgba(89,201,244,.35)",
                        animation: "ob-rotate-slow 24s linear infinite",
                      }}
                    />
                  </div>

                  <div style={{ ...type.label, fontSize: 10.5, letterSpacing: ".22em", color: C.blueAccent, fontWeight: 700, marginBottom: 8 }}>
                    STEP 0{i + 1}
                  </div>
                  <h3 style={{ fontFamily: F.h, fontSize: 22, fontWeight: 600, color: "#fff", marginBottom: 8 }}>{step.t}</h3>
                  <p style={{ fontSize: 13.5, lineHeight: 1.55, color: "rgba(255,255,255,.65)", maxWidth: 240, margin: "0 auto" }}>{step.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const CHI_LOGOS = [
  { src: GRID_PLUS_LOGO, alt: "GRID+", height: (m) => (m ? 36 : 44) },
  { src: OBSCURA_LOGO, alt: "OBSCURA", height: (m) => (m ? 46 : 56) },
  { src: SAPIENZA_LOGO_DARK, alt: "Startup Sapienza Università di Roma", height: (m) => (m ? 38 : 46) },
];

function ChiSiamo({ isMobile, isTablet, pageWrap }) {
  return (
    <section
      id="chi-siamo"
      style={{
        padding: isMobile ? "56px 0 52px" : "72px 0 68px",
        position: "relative",
        zIndex: 1,
        background: C.white,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div style={pageWrap}>
        <Reveal>
          <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
            <STitle style={{ margin: "0 0 16px", letterSpacing: "-.02em" }}>
              Un prodotto GRID+ con radici nella ricerca
            </STitle>
            <SDesc style={{ margin: "0 auto" }}>
              OBSCURA unisce l&apos;esperienza di{" "}
              <a
                href="https://www.gridplus.it"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: C.smartBlue, fontWeight: 500, textDecoration: "none" }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
              >
                GRID+
              </a>{" "}
              su documenti regolati e automazioni con la collaborazione scientifica di{" "}
              <strong style={{ fontWeight: 500 }}>Sapienza Università di Roma</strong>
              {" "}— per uno strato di anonimizzazione affidabile prima dell&apos;AI.
            </SDesc>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobile ? 28 : 40,
                marginTop: isMobile ? 32 : 40,
              }}
            >
              {CHI_LOGOS.map((logo) => (
                <img
                  key={logo.alt}
                  src={logo.src}
                  alt={logo.alt}
                  style={{
                    height: logo.height(isMobile),
                    width: "auto",
                    maxWidth: 180,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function PremiumPricing({ isMobile, isTablet, pageWrap, onNavigate, onRequestQuote, onAbbonati, account }) {
  // Il listino arriva dal backend (piani.py e' l'unica fonte); la copia
  // statica fa comparire la sezione anche se il backend non risponde.
  const [cadenza, setCadenza] = useState("mensile");
  const [listino, setListino] = useState(LISTINO_STATICO);
  const [hover, setHover] = useState(null);
  useEffect(() => {
    let vivo = true;
    fetch("/api/abbonamenti/piani")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivo && Array.isArray(d?.piani) && d.piani.length) setListino(d.piani); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);
  const abbonata = !!account?.azienda?.abbonata;
  const piani = listino.filter((p) => p.chiave !== "enterprise");
  const enterprise = listino.find((p) => p.chiave === "enterprise");
  // Quattro schede in fila; Enterprise sotto, larga: cinque colonne erano
  // troppo strette per leggersi.
  const colonne = isMobile ? "1fr" : isTablet ? "1fr 1fr" : `repeat(${piani.length}, minmax(0, 1fr))`;
  // Chi ha un abbonamento vede il listino alla cadenza del suo piano, cosi'
  // la scheda "Piano attuale" e' quella giusta al primo sguardo.
  const cadenzaAzienda = abbonata ? account.azienda.cadenza : null;
  useEffect(() => { if (cadenzaAzienda) setCadenza(cadenzaAzienda); }, [cadenzaAzienda]);
  const abbonabili = pianiAbbonabili(listino);
  const spunta = (light) => <Ico size={16} color={light ? "#fff" : C.smartBlue}><polyline points="20 6 9 17 4 12"/></Ico>;
  const prezzoEnterprise = enterprise ? prezzoPerCadenza(enterprise, cadenza) : null;
  return (
    <section id="prezzi" style={{ padding: "88px 0 72px", position: "relative", zIndex: 1, background: C.sectionAlt }}>
      <div style={pageWrap}>
        <div style={{ textAlign: "center", marginBottom: 30 }}>
          <Reveal><STitle style={{ fontSize: isMobile ? 34 : 48, textAlign: "center" }}>Scegli il piano giusto per te</STitle></Reveal>
          <Reveal delay={80}>
            <SDesc style={{ margin: "0 auto", maxWidth: 640, textAlign: "center" }}>
              Seleziona la soluzione pi&ugrave; adatta: prezzi a consumo attivabili in autonomia, oppure un&apos;offerta dedicata e personalizzata su misura per la tua azienda.
            </SDesc>
          </Reveal>
          <Reveal delay={120}>
            <SelettoreCadenza cadenza={cadenza} onChange={setCadenza} style={{ marginTop: 22 }} />
          </Reveal>
        </div>
        <Reveal delay={140} style={{ width: "100%" }}>
          <div style={{ display: "grid", gridTemplateColumns: colonne, gap: isMobile ? 16 : 18, alignItems: "stretch", width: "100%" }}>
            {piani.map((p) => {
              const inEvidenza = p.chiave === PIANO_IN_EVIDENZA;
              let etichetta;
              let azione;
              let sopra = inEvidenza ? "PIÙ SCELTO" : null;
              let variante = inEvidenza ? "blu" : "scuro";
              let attuale = false;
              if (p.chiave === "web") { etichetta = "Prova ora"; azione = onNavigate; }
              else if (abbonata) {
                // Il piano in corso e' spento; gli altri portano all'area
                // clienti, dove il cambio si completa nel portale Stripe.
                const a = azionePiano(p, cadenza, account.azienda, abbonabili);
                etichetta = a.etichetta;
                attuale = a.tipo === "attuale";
                if (attuale) { sopra = "IL TUO PIANO"; variante = "spento"; }
                else if (a.tipo === "su") { sopra = "UPGRADE"; variante = "blu"; }
                else if (a.tipo === "giu" || a.tipo === "cadenza") variante = "chiaro";
                azione = () => onAbbonati(p.chiave, cadenza);
              } else { etichetta = `Attiva ${p.nome}`; azione = () => onAbbonati(p.chiave, cadenza); }
              return (
                <SchedaPiano
                  key={p.chiave}
                  piano={p}
                  cadenza={cadenza}
                  tinta={TINTE[p.chiave]}
                  evidenza={inEvidenza || attuale}
                  etichettaAlto={sopra}
                  isMobile={isMobile}
                  bottone={<BottonePiano etichetta={etichetta} onClick={azione} variante={variante} />}
                />
              );
            })}
          </div>
        </Reveal>
        {enterprise && (
          <Reveal delay={180} style={{ width: "100%" }}>
            <div
              onMouseEnter={() => setHover("enterprise")}
              onMouseLeave={() => setHover(null)}
              style={{
                marginTop: 18, borderRadius: 20, padding: isMobile ? "24px 20px" : "28px 32px",
                background: "linear-gradient(155deg, #061427 0%, #102845 45%, #182E4F 100%)", color: "#fff",
                border: hover === "enterprise" ? "1px solid rgba(89,201,244,0.45)" : "1px solid rgba(89,201,244,0.28)",
                boxShadow: "0 20px 48px rgba(6,20,39,0.18)",
                display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "minmax(200px, 0.9fr) 2fr auto",
                gap: isMobile ? 18 : 28, alignItems: "center", position: "relative", overflow: "hidden",
              }}
            >
              <div style={{ position: "absolute", top: -40, right: -60, width: 180, height: 180, borderRadius: "50%", background: "radial-gradient(circle, rgba(89,201,244,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
              <div style={{ position: "relative", zIndex: 1 }}>
                <h3 style={{ fontFamily: F.h, fontSize: 22, fontWeight: 600, letterSpacing: "0.1em", margin: "0 0 10px", textTransform: "uppercase" }}>{enterprise.nome}</h3>
                <div style={{ fontFamily: F.h, fontSize: 30, fontWeight: 800, letterSpacing: "-0.5px", lineHeight: 1.05 }}>{prezzoEnterprise.importo}</div>
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "rgba(255,255,255,0.72)", fontFamily: F.b }}>{enterprise.per || `${prezzoEnterprise.dettaglio}. ${enterprise.pagine}.`}</p>
              </div>
              <div style={{ position: "relative", zIndex: 1, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "8px 18px" }}>
                {enterprise.voci.map((v) => (
                  <div key={v.testo} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                    <span style={{ flexShrink: 0, marginTop: 2 }}>{spunta(true)}</span>
                    <span style={{ fontSize: 13, lineHeight: "20px", color: "rgba(255,255,255,0.88)", fontFamily: F.b }}>{v.testo}</span>
                  </div>
                ))}
              </div>
              <div style={{ position: "relative", zIndex: 1, gridColumn: isTablet && !isMobile ? "1 / -1" : undefined }}>
                <GBtn variant="white" onClick={onRequestQuote} disableMagnet style={{ width: isMobile || isTablet ? "100%" : undefined, justifyContent: "center", height: 46, borderRadius: 12, padding: "0 22px", whiteSpace: "nowrap" }}>Richiedi un preventivo</GBtn>
              </div>
            </div>
          </Reveal>
        )}
        <Reveal delay={190}>
          {/* Le note fiscali una volta sola, sotto il listino: nelle schede
              c'e' scritto a chi si rivolge ciascun piano. */}
          <p style={{ margin: "22px auto 0", maxWidth: 640, textAlign: "center", fontSize: 12, color: C.textNum, fontFamily: F.b, lineHeight: 1.5 }}>{NOTA_LISTINO}</p>
        </Reveal>
        <Reveal delay={200}>
          <StripeTrustLine style={{ marginTop: 16, textAlign: "center", maxWidth: 640, marginLeft: "auto", marginRight: "auto" }} />
        </Reveal>
      </div>
    </section>
  );
}

function FinalCTACard({ onNavigate, compactHeading, isMobile }) {
  const ctaStyle = isMobile
    ? {
        display: "flex",
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        justifyContent: "center",
        padding: "0 18px",
        height: 54,
        fontSize: 14,
      }
    : undefined;

  return (
    <Reveal>
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          textAlign: "center",
          borderRadius: 28,
          padding: isMobile ? "48px 20px" : "58px 28px",
          background: "linear-gradient(155deg, #061427 0%, #102845 50%, #182E4F 100%)",
          border: "1px solid rgba(89,201,244,0.28)",
          boxShadow: "0 28px 64px rgba(6,20,39,0.28), 0 12px 32px rgba(89,201,244,0.10)",
          color: "#fff",
          boxSizing: "border-box",
        }}
      >
        <AuroraDark intensity={0.5} />
        <GridOverlay color="rgba(255,255,255,.06)" size={64} opacity={0.5} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <STitle
            light
            style={{
              fontSize: compactHeading ? 32 : 40,
              letterSpacing: "-.02em",
              marginBottom: 16,
            }}
          >
            Processa i tuoi documenti senza esporre{" "}
            <span style={{ color: C.blueAccent }}>dati sensibili.</span>
          </STitle>
          <SDesc light style={{ maxWidth: 600, margin: "0 auto 28px", textAlign: "center" }}>
            Affidati a un sistema di anonimizzazione professionale per eliminare i rischi di data breach e rendere la tua azienda davvero compliant.
          </SDesc>
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <GBtn variant="hero" onClick={onNavigate} iconRight={!isMobile} disableMagnet style={ctaStyle}>Anonimizza un documento</GBtn>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, marginTop: 26 }}>
            {["Compliant con il GDPR"].map((tag) => (
              <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 13px", borderRadius: 999, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.14)" }}>
                <Ico size={13} color={C.blueAccent}><polyline points="20 6 9 17 4 12"/></Ico>
                <span style={{ ...type.label, fontSize: 10.5, letterSpacing: ".1em", color: "rgba(255,255,255,0.82)" }}>{tag}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

function FaqRow({ faq, isOpen, onToggle, isMobile, isTablet, isLast = false }) {
  const contentRef = useRef(null);
  const [maxH, setMaxH] = useState(0);
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const measure = () => setMaxH(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [faq.a]);
  return (
    <div style={{ borderBottom: isLast ? "none" : `1px solid ${C.border}` }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        style={{
          width: "100%",
          padding: isMobile ? "20px 0" : "26px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 20,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: F.h,
          fontSize: isMobile ? 20 : isTablet ? 21 : 24,
          lineHeight: 1.25,
          fontWeight: 400,
          color: C.textTitle,
        }}
      >
        <span>{faq.q}</span>
        <span style={{ flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: `transform 0.3s ${ease}`, display: "inline-flex" }}>
          <Ico size={24} color={C.textTitle}><polyline points="6 9 12 15 18 9"/></Ico>
        </span>
      </button>
      <div style={{ maxHeight: isOpen ? maxH : 0, overflow: "hidden", transition: `max-height 0.35s ${ease}` }}>
        <div ref={contentRef} style={{ paddingBottom: 24 }}>
          <p style={{ margin: 0, maxWidth: isMobile ? "none" : 560, fontSize: 16, lineHeight: "27px", color: C.textNum, fontWeight: 400 }}>{faq.a}</p>
        </div>
      </div>
    </div>
  );
}

function getNavLayout(width) {
  const navFont = 12;
  if (width < 768) {
    return {
      logoW: 128,
      barH: 52,
      showLinks: false,
      linkFont: navFont,
      linkGap: 0,
      linkPadX: 0,
      ctaLabel: "Anonimizza",
      ctaH: 36,
      ctaFont: navFont,
      ctaPadX: 14,
      padX: 12,
      padInnerL: 12,
      padInnerR: 8,
      actionsGap: 8,
    };
  }
  if (width < 1100) {
    return {
      logoW: 148,
      barH: 56,
      showLinks: false,
      linkFont: navFont,
      linkGap: 0,
      linkPadX: 0,
      ctaLabel: "Anonimizza",
      ctaH: 36,
      ctaFont: navFont,
      ctaPadX: 16,
      padX: 16,
      padInnerL: 14,
      padInnerR: 8,
      actionsGap: 10,
    };
  }
  if (width < 1280) {
    return {
      logoW: 162,
      barH: 58,
      showLinks: true,
      linkFont: navFont,
      linkGap: 12,
      linkPadX: 2,
      ctaLabel: "Anonimizza documento",
      ctaH: 36,
      ctaFont: navFont,
      ctaPadX: 16,
      padX: 20,
      padInnerL: 16,
      padInnerR: 6,
      actionsGap: 14,
    };
  }
  return {
    logoW: 176,
    barH: 62,
    showLinks: true,
    linkFont: navFont,
    linkGap: 22,
    linkPadX: 2,
    ctaLabel: "Anonimizza un documento",
    ctaH: 38,
    ctaFont: navFont,
    ctaPadX: 18,
    padX: 20,
    padInnerL: 18,
    padInnerR: 6,
    actionsGap: 20,
  };
}

// ACCEDI / AREA CLIENTI nella top bar. All'hover il gradiente rovesciato
// compare con l'opacita', come negli altri pulsanti (StratoHover): prima i
// due gradienti si scambiavano di colpo.
function BottoneAccount({ onClick, style, children }) {
  const [sopra, setSopra] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...style, position: "relative" }}
      onMouseEnter={() => setSopra(true)}
      onMouseLeave={() => setSopra(false)}
      onFocus={(e) => { if (focusDaTastiera(e)) setSopra(true); }}
      onBlur={() => setSopra(false)}
    >
      <StratoHover sfondo={NAV_ACCOUNT_HOVER} acceso={sopra} bordo={1} />
      <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 8 }}>{children}</span>
    </button>
  );
}

function LandingNav({
  width,
  pageWrap,
  navLinks,
  onNavigate,
  mobileMenuOpen,
  setMobileMenuOpen,
  account,
  onAccount,
}) {
  const etichettaAccount = account ? "Area clienti" : "Accedi";
  // L'omino a sinistra dell'etichetta: e' il pulsante del proprio account,
  // con o senza accesso fatto.
  const iconaAccount = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
  );
  const [portalReady, setPortalReady] = useState(false);
  useEffect(() => setPortalReady(true), []);

  const nav = getNavLayout(width);
  const showMenu = !nav.showLinks;

  const navBarStyle = {
    backgroundColor: NAV_GRID.bg,
    borderRadius: NAV_GRID.radius,
    boxShadow: NAV_GRID.shadow,
    paddingLeft: nav.padInnerL,
    paddingRight: nav.padInnerR,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    minHeight: nav.barH,
  };
  const navLinkStyle = {
    color: NAV_GRID.link,
    textDecoration: "none",
    fontSize: nav.linkFont,
    fontWeight: 400,
    fontFamily: F.h,
    letterSpacing: "0.03em",
    textTransform: "uppercase",
    transition: "color 0.1s ease-in-out",
    whiteSpace: "nowrap",
    padding: `0 ${nav.linkPadX}px`,
  };
  const navCtaStyle = {
    height: nav.ctaH,
    padding: `0 ${nav.ctaPadX}px`,
    fontSize: nav.ctaFont,
    fontWeight: btnBase.fontWeight,
    fontFamily: btnBase.fontFamily,
    borderRadius: btnBase.borderRadius,
    justifyContent: "center",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    background: NAV_GRID.btn,
    color: "#fff",
    border: "1px solid transparent",
    boxShadow: "none",
    transform: "none",
    flexShrink: 0,
  };
  // Senza alone, come il pulsante principale accanto: l'ombra, tagliata dal
  // contenitore dei link, si vedeva come un rettangolo attorno al pulsante.
  const navAccountStyle = {
    ...navCtaStyle,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: NAV_ACCOUNT_BG,
    cursor: "pointer",
  };

  const navShell = (
    <nav
      aria-label="Navigazione principale"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9800,
        padding: `max(${NAV_TOP_GAP}px, env(safe-area-inset-top, 0px)) ${nav.padX}px 0`,
        pointerEvents: "none",
        isolation: "isolate",
      }}
    >
      <div style={{ ...pageWrap, pointerEvents: "auto", paddingTop: 0, paddingBottom: 0 }}>
        <div style={navBarStyle}>
          <a href="#" style={{ display: "flex", alignItems: "center", flexShrink: 0, minWidth: 0, textDecoration: "none", position: "relative", zIndex: 2 }}>
            <ProductMark nav navWidth={nav.logoW} />
          </a>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: nav.actionsGap,
              flexShrink: 1,
              minWidth: 0,
              marginLeft: "auto",
              justifyContent: "flex-end",
              position: "relative",
              zIndex: 2,
            }}
          >
            {nav.showLinks && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: nav.linkGap,
                  marginRight: 4,
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                {navLinks.map(([l, href]) => (
                  <a
                    key={l}
                    href={href}
                    style={navLinkStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.color = NAV_GRID.linkHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = NAV_GRID.link; }}
                  >
                    {l}
                  </a>
                ))}
              </div>
            )}
            {/* Fuori dal contenitore dei link, che nasconde quello che sborda.
                I margini lasciano le distanze di prima: dall'ultimo link come
                fra due link (+4), dal pulsante principale come prima. */}
            {nav.showLinks && (
              <BottoneAccount
                onClick={onAccount}
                style={{ ...navAccountStyle, marginLeft: nav.linkGap - nav.actionsGap, marginRight: 4 }}
              >
                {iconaAccount}{etichettaAccount}
              </BottoneAccount>
            )}

            <GBtn variant="primary" size="sm" onClick={onNavigate} disableMagnet iconRight={!showMenu} style={navCtaStyle}>
              {nav.ctaLabel}
            </GBtn>
            {showMenu && (
              <button
                type="button"
                aria-label={mobileMenuOpen ? "Chiudi menu" : "Apri menu"}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                onClick={() => setMobileMenuOpen((o) => !o)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  padding: 0,
                  flexShrink: 0,
                  background: mobileMenuOpen ? "rgba(0,156,221,0.12)" : "rgba(255,255,255,0.65)",
                  border: "1px solid rgba(222, 240, 249, 0.9)",
                  borderRadius: NAV_GRID.radius,
                  cursor: "pointer",
                  color: NAV_GRID.link,
                  transition: `background 0.25s ${ease}`,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {mobileMenuOpen
                    ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                    : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
                </svg>
              </button>
            )}
          </div>
        </div>

        {showMenu && (
          <div
            id="mobile-menu"
            style={{
              overflow: "hidden",
              maxHeight: mobileMenuOpen ? 480 : 0,
              opacity: mobileMenuOpen ? 1 : 0,
              marginTop: mobileMenuOpen ? 10 : 0,
              transition: `max-height 0.4s ${ease}, opacity 0.3s ${ease}, margin-top 0.3s ${ease}`,
              backgroundColor: NAV_GRID.bg,
              borderRadius: NAV_GRID.radius,
              boxShadow: NAV_GRID.shadow,
            }}
          >
              <div style={{ display: "flex", flexDirection: "column", padding: "12px 18px 16px" }}>
              {navLinks.map(([l, href]) => (
                <a
                  key={l}
                  href={href}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    ...navLinkStyle,
                    padding: "13px 4px",
                    borderBottom: "1px solid rgba(222, 240, 249, 0.9)",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = NAV_GRID.linkHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = NAV_GRID.link; }}
                >
                  {l}
                </a>
              ))}
              <BottoneAccount
                onClick={() => { setMobileMenuOpen(false); onAccount(); }}
                style={{ ...navAccountStyle, width: "100%", justifyContent: "center", margin: "12px 0 2px" }}
              >
                {iconaAccount}{etichettaAccount}
              </BottoneAccount>
            </div>
          </div>
        )}
      </div>
    </nav>
  );

  return portalReady && typeof document !== "undefined"
    ? createPortal(navShell, document.body)
    : navShell;
}

function ContactForm({ isMobile }) {
  const [contactForm, setContactForm] = useState({
    nome: "",
    email: "",
    azienda: "",
    messaggio: "",
  });
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactStatus, setContactStatus] = useState({ type: "", message: "" });
  const handleContactFieldChange = (event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
    if (contactStatus.type) setContactStatus({ type: "", message: "" });
  };
  const submitContactForm = async () => {
    if (isContactSubmitting) return;
    const trimmedPayload = {
      nome: contactForm.nome.trim(),
      email: contactForm.email.trim(),
      azienda: contactForm.azienda.trim(),
      messaggio: contactForm.messaggio.trim(),
    };
    if (!trimmedPayload.nome || !trimmedPayload.email || !trimmedPayload.azienda || !trimmedPayload.messaggio) {
      setContactStatus({ type: "error", message: "Compila tutti i campi prima di inviare." });
      return;
    }
    setIsContactSubmitting(true);
    setContactStatus({ type: "", message: "" });
    try {
      const formData = new FormData();
      formData.append("nome", trimmedPayload.nome);
      formData.append("email", trimmedPayload.email);
      formData.append("azienda", trimmedPayload.azienda);
      formData.append("messaggio", trimmedPayload.messaggio);
      const response = await fetch("/api/contact", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        throw httpError(response.status, "contact");
      }
      setContactStatus({ type: "success", message: "Messaggio inviato. Ti ricontatteremo al piu presto." });
      setContactForm({ nome: "", email: "", azienda: "", messaggio: "" });
    } catch (error) {
      setContactStatus({ type: "error", message: toFriendlyError(error, "contact") });
    } finally {
      setIsContactSubmitting(false);
    }
  };
  return (
    <div
      style={{
        background: C.white,
        borderRadius: C.radiusCard,
        padding: isMobile ? "24px 18px" : "32px 28px",
        boxShadow: C.shadow,
        border: `1px solid ${C.border}`,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {[
        { label: "Nome e cognome", name: "nome", type: "text" },
        { label: "Email aziendale", name: "email", type: "email" },
        { label: "Azienda", name: "azienda", type: "text" },
      ].map(({ label, name, type }) => (
        <div key={name} style={{ marginBottom: 16 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 500,
              color: C.textNum,
              marginBottom: 5,
            }}
          >
            {label}
          </label>

          <input
            name={name}
            type={type}
            placeholder={label}
            value={contactForm[name]}
            onChange={handleContactFieldChange}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "11px 14px",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              fontSize: 14,
              color: C.textTitle,
              background: C.smartWhite,
              outline: "none",
              fontFamily: "inherit",
              fontWeight: 400,
            }}
            onFocus={(e) => (e.target.style.borderColor = C.smartBlue)}
            onBlur={(e) => (e.target.style.borderColor = C.border)}
          />
        </div>
      ))}

      <div style={{ marginBottom: 18 }}>
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 500,
            color: C.textNum,
            marginBottom: 5,
          }}
        >
          Messaggio
        </label>

        <textarea
          name="messaggio"
          rows={3}
          placeholder="Descrivi il tuo caso d'uso..."
          value={contactForm.messaggio}
          onChange={handleContactFieldChange}
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "11px 14px",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            fontSize: 14,
            color: C.textTitle,
            background: C.smartWhite,
            outline: "none",
            resize: "vertical",
            fontFamily: "inherit",
            fontWeight: 400,
          }}
          onFocus={(e) => (e.target.style.borderColor = C.smartBlue)}
          onBlur={(e) => (e.target.style.borderColor = C.border)}
        />
      </div>

      <GBtn
        variant="pill"
        onClick={submitContactForm}
        style={{
          width: "100%",
          justifyContent: "center",
          height: 50,
          borderRadius: 12,
          opacity: isContactSubmitting ? 0.7 : 1,
          pointerEvents: isContactSubmitting ? "none" : "auto",
        }}
      >
        {isContactSubmitting ? "Invio..." : "Invia"}
      </GBtn>

      {contactStatus.message && (
        <div
          style={{
            marginTop: 12,
            padding: "10px 12px",
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 500,
            color: contactStatus.type === "success" ? "#107649" : "#c0392b",
            background: contactStatus.type === "success" ? "#eafaf1" : "#fff0f0",
            border:
              contactStatus.type === "success"
                ? "1px solid #bfe8d0"
                : "1px solid #f5c6c6",
          }}
        >
          {contactStatus.message}
        </div>
      )}
    </div>
  );
}

function QuoteModal({ isMobile, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(6,20,39,0.55)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: isMobile ? "24px 16px" : "48px 24px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 520,
          margin: "auto",
        }}
      >
        <div style={{ marginBottom: 16, color: "#fff" }}>
          <h3 style={{ fontFamily: F.h, fontSize: isMobile ? 24 : 28, fontWeight: 600, margin: 0 }}>Richiedi un preventivo</h3>
          <p style={{ fontFamily: F.b, fontSize: 14, lineHeight: 1.5, margin: "8px 0 0", color: "rgba(255,255,255,0.8)" }}>
            Compila il form e parlaci del tuo progetto. Ti proporremo la soluzione piu adatta al tuo contesto.
          </p>
        </div>
        <button
          type="button"
          aria-label="Chiudi"
          onClick={onClose}
          style={{
            position: "absolute",
            top: isMobile ? -4 : 0,
            right: 0,
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.35)",
            background: "rgba(255,255,255,0.08)",
            color: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          <Ico size={16} color="#fff"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>
        </button>
        <ContactForm isMobile={isMobile} />
      </div>
    </div>,
    document.body
  );
}

function LandingPage({ onNavigate, onNavigateTo }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { width, isMobile, isTablet } = useViewport();
  const pageWrap = getWrapStyle(width);
  const { account } = useAccount();
  const vaiAllAccount = () => onNavigateTo(account ? "account" : "accesso");
  // Dalla scheda prezzi: chi non ha un account si registra col piano gia'
  // scelto; chi ce l'ha lo trova evidenziato in area clienti.
  const abbonati = (piano, cadenza) => {
    ricordaPianoScelto(piano, cadenza);
    onNavigateTo(account ? "account" : "registrazione", account ? {} : { piano, cadenza });
  };
  const navLinks = [
    ["Come funziona", "#come-funziona"],
    ["Chi siamo", "#chi-siamo"],
    ["Prezzi", "#prezzi"],
    ["Contatti", "#contatti"],
  ];
  useEffect(() => {
    if (width >= 1100 && mobileMenuOpen) setMobileMenuOpen(false);
  }, [width, mobileMenuOpen]);
  const contactIconColor = C.dataNight;
  const contactItems = [
    { label: "Email", value: "info@gridplus.it", href: "mailto:info@gridplus.it", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill={contactIconColor} aria-hidden="true"><path d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm8 8.1L4.55 7H4v.7l8 6.55 8-6.55V7h-.55L12 13.1Z"/></svg> },
    { label: "Telefono", value: "+39 3387655663", href: "tel:+393387655663", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill={contactIconColor} aria-hidden="true"><path d="M6.62 10.79a15.1 15.1 0 0 0 6.59 6.59l2.2-2.2a1.5 1.5 0 0 1 1.54-.36c1.7.57 3.2.86 4.55.86.83 0 1.5.67 1.5 1.5V21.5c0 .83-.67 1.5-1.5 1.5C10.2 23 1 13.8 1 2.5 1 1.67 1.67 1 2.5 1h4.32c.83 0 1.5.67 1.5 1.5 0 1.35.29 2.85.86 4.55.17.53.03 1.11-.36 1.54l-2.2 2.2Z"/></svg> },
    { label: "Indirizzo", value: "Via Andrea Ferrara, 45, Roma (RM), 00165", icon: <svg width="22" height="22" viewBox="0 0 24 24" fill={contactIconColor} aria-hidden="true"><path d="M12 2a8 8 0 0 0-8 8c0 5.25 6.35 11.1 7.4 12.03a.9.9 0 0 0 1.2 0C13.65 21.1 20 15.25 20 10a8 8 0 0 0-8-8Zm0 11.2a3.2 3.2 0 1 1 0-6.4 3.2 3.2 0 0 1 0 6.4Z"/></svg> },
  ];
  const faqs = [
    {
      q: "Quali documenti possiamo anonimizzare?",
      a: "Contratti, CV, referti, fatture, dataset e documenti amministrativi con dati personali, finanziari, legali o sanitari. OBSCURA accetta file PDF, DOCX, CSV, PPTX, PNG e JPG, e riconosce 58 categorie di entità sensibili ottimizzate per l'italiano (nomi, IBAN, codice fiscale, indirizzi e altro), preservando il contenuto utile per analisi e automazioni.",
    },
    {
      q: "OBSCURA può anonimizzare anche le foto?",
      a: "Sì. Oltre ai dati testuali, OBSCURA può elaborare immagini (PNG, JPG) e documenti con foto, come scansioni di carte d'identità o CV. Con l'opzione 'Sfoca i volti' rileva e sfoca automaticamente i volti presenti, rendendoli non identificabili, oltre all'anonimizzazione delle categorie di dati sensibili che selezioni.",
    },
    {
      q: "In che modo OBSCURA riduce il rischio GDPR?",
      a: "Anonimizza i dati sensibili prima che i documenti entrino in strumenti AI, RAG o workflow condivisi: meno superficie esposta, meno rischio di violazione. L'elaborazione è in tempo reale su server EU, con cancellazione automatica entro un'ora. Nessun file resta sui nostri server, in linea con privacy by design e con i requisiti di GDPR, AI Act e NIS2.",
    },
    {
      q: "Usate API di terze parti?",
      a: "OBSCURA utilizza un motore proprietario Grid+, ottimizzato per documenti italiani e dati sensibili. I file non vengono inviati a OpenAI, ChatGPT né ad altre API AI pubbliche di terze parti. Inoltre, i documenti caricati non vengono usati per addestrare o migliorare modelli di intelligenza artificiale.",
    },
    {
      q: "I miei dati sono al sicuro?",
      a: "I documenti viaggiano su connessione cifrata (TLS) verso infrastruttura cloud Amazon Web Services (AWS) nell'Unione Europea, senza trasferimenti extra-UE per l'elaborazione. I pagamenti online sono gestiti da Stripe: i dati della carta non transitano sui server di Obscura o GRID+.",
    },
    {
      q: "OBSCURA può integrarsi con infrastrutture aziendali?",
      a: "Sì. Puoi usarlo in autonomia dalla web app, via REST API nei processi documentali esistenti, o in deployment on-premise, VPC o cloud dedicato per ambienti regolati. Si adatta a pipeline HR, legal, sanitario e AI senza sostituire i tuoi sistemi.",
    },
    {
      q: "Possiamo valutare un caso d'uso enterprise?",
      a: "Sì. Compila il form in fondo alla pagina indicando volumi, vincoli di deployment e integrazioni richieste: ti rispondiamo con un preventivo su misura, regole di anonimizzazione personalizzate e supporto operativo dedicato.",
    },
  ];
  return (
    <div style={{ background: C.bg, fontFamily: F.b, color: C.textBody, position: "relative" }}>
      <LandingNav
        width={width}
        pageWrap={pageWrap}
        navLinks={navLinks}
        onNavigate={onNavigate}
        account={account}
        onAccount={vaiAllAccount}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      {!isMobile && (
        <div aria-hidden style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
          <DecoLines variant={1} style={{ top: "55vh", right: "-80px", width: "480px", height: "680px", zIndex: 0 }}/>
          <DecoLines variant={2} style={{ top: "200vh", left: "-100px", width: "400px", height: "580px", zIndex: 0 }}/>
          <DecoLines variant={1} style={{ top: "350vh", right: "-60px", width: "440px", height: "600px", transform: "scaleX(-1)", zIndex: 0 }}/>
        </div>
      )}

      <PremiumHero onNavigate={onNavigate} isMobile={isMobile} isTablet={isTablet} pageWrap={pageWrap} />
      <PiiMarquee isMobile={isMobile} isTablet={isTablet} pageWrap={pageWrap} />

      <WhyRail isMobile={isMobile} isTablet={isTablet} onNavigate={onNavigate} />

      {/* ═══ PER CHI È (5 sectors) ═══ */}
      <section id="per-chi" style={{ padding: "80px 0 70px", position: "relative", zIndex: 1, background: C.smartWhite }}>
        <div style={pageWrap}>
          <div style={{ textAlign: "center", marginBottom: 20 }}><Reveal><STitle>Per chi è OBSCURA</STitle></Reveal><Reveal delay={80}><SDesc style={{ margin: "0 auto" }}>Un'unica soluzione per la privacy documentale, adatta a qualunque settore.</SDesc></Reveal></div>
          <Reveal delay={150}><TabSection onNavigate={onNavigate} isMobile={isMobile} isTablet={isTablet}/></Reveal>
        </div>
      </section>

      <WorkflowHorizon isMobile={isMobile} isTablet={isTablet} pageWrap={pageWrap} />
      <ChiSiamo isMobile={isMobile} isTablet={isTablet} pageWrap={pageWrap} />
      <PremiumPricing isMobile={isMobile} isTablet={isTablet} pageWrap={pageWrap} onNavigate={onNavigate} onRequestQuote={() => setQuoteOpen(true)} onAbbonati={abbonati} account={account} />

      {/* ═══ TESTIMONIALS + CONTATTI ═══ */}
      <section style={{ padding: isMobile ? "80px 0 52px" : "80px 0 40px", position: "relative", zIndex: 1, background: C.white }}>
        <div style={pageWrap}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "flex-start", marginBottom: 40, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 14 : 0 }}>
            <Reveal><STitle>Cosa dicono di OBSCURA</STitle></Reveal>
            <Reveal delay={100}>
              {/* <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: C.dataNight, borderRadius: 50, padding: "8px 20px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.blueAccent }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: "#fff", textTransform: "uppercase", letterSpacing: "1px" }}>Recensioni</span>
              </div> */}
            </Reveal>
          </div>

          {/* Carousel */}
          <Reveal delay={100}>
            <TestimonialCarousel isMobile={isMobile} />
          </Reveal>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr" : "repeat(4, minmax(0, 1fr))",
              gap: isMobile ? 24 : 20,
              alignItems: "start",
              marginTop: isMobile ? 40 : 84,
            }}
          >
            {/* CONTACT LEFT — columns 1 and 2 */}
            <div
              id="contatti"
              style={{
                gridColumn: isMobile ? "1 / -1" : isTablet ? "1 / -1" : "1 / span 2",
                marginTop: 0,
                minWidth: 0,
              }}
            >
              <Reveal>
                <STitle>Contattaci</STitle>
              </Reveal>

              <Reveal delay={80}>
                <SDesc style={{ margin: 0 }}>
                  Compila il form e parlaci del tuo progetto.<br />Ti proporremo la soluzione piu adatta al tuo contesto.
                </SDesc>
              </Reveal>

              <Reveal delay={150}>
                <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 28, maxWidth: 520 }}>
                  {contactItems.map((item) => {
                    const content = (
                      <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "4px 0" }}>
                        <span style={{ width: 28, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{item.icon}</span>
                        <span>
                          {/* <span style={{ display: "block", fontSize: 11, fontWeight: 800, color: C.textNum, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 3 }}>{item.label}</span> */}
                          <span style={{ display: "block", fontSize: 16, lineHeight: "25.6px", color: C.textNum, fontWeight: 400 }}>{item.value}</span>
                        </span>
                      </div>
                    );
                    return item.href ? <a key={item.label} href={item.href} style={{ textDecoration: "none" }}>{content}</a> : <div key={item.label}>{content}</div>;
                  })}
                </div>
              </Reveal>
            </div>

            {/* CONTACT FORM — columns 3 and 4 */}
            <div
              style={{
                gridColumn: isMobile ? "1 / -1" : isTablet ? "1 / -1" : "3 / span 2",
                marginTop: 0,
                minWidth: 0,
                width: "100%",
              }}
            >
              <Reveal delay={150}>
                <ContactForm isMobile={isMobile} />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" style={{ padding: isMobile ? "72px 0 64px" : "88px 0 72px", position: "relative", zIndex: 1, background: C.white }}>
        <div style={pageWrap}>
          <div style={{ maxWidth: 760, margin: "0 auto" }}>
            <Reveal>
              <STitle style={{ fontSize: isMobile ? 34 : isTablet ? 38 : 44, textAlign: "center" }}>FAQ</STitle>
              <SDesc style={{ margin: "0 auto 36px", textAlign: "center" }}>
                Documenti e formati supportati, conformità normativa, integrazioni e piani su misura.
              </SDesc>
            </Reveal>
            <Reveal delay={80}>
              <div
                style={{
                  background: C.smartWhite,
                  borderRadius: 20,
                  border: `1px solid ${C.border}`,
                  padding: isMobile ? "8px 20px 4px" : "12px 32px 4px",
                  boxShadow: C.shadow,
                }}
              >
                {faqs.map((faq, i) => (
                  <FaqRow
                    key={faq.q}
                    faq={faq}
                    isOpen={openFaq === i}
                    onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                    isMobile={isMobile}
                    isTablet={isTablet}
                    isLast={i === faqs.length - 1}
                  />
                ))}
              </div>
            </Reveal>
            <Reveal delay={140}>
              <p style={{ marginTop: 28, fontSize: 14, lineHeight: 1.6, color: C.textNum, fontFamily: F.b, textAlign: "center" }}>
                Non trovi una risposta?{" "}
                <a href="#contatti" style={{ color: C.smartBlue, fontWeight: 700, textDecoration: "none" }}>Scrivici, ti rispondiamo noi.</a>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section style={{ padding: isMobile ? "64px 0 80px" : "72px 0 96px", position: "relative", zIndex: 1, background: C.sectionAlt }}>
        <div style={pageWrap}>
          <FinalCTACard onNavigate={onNavigate} compactHeading={isMobile} isMobile={isMobile} />
        </div>
      </section>
      {/* FOOTER */}
      <footer style={{ background: "linear-gradient(180deg, #061427 0%, #0B1D34 48%, #182E4F 100%)", padding: isMobile ? "46px 0 30px" : "64px 0 34px", position: "relative", zIndex: 1, overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.18, backgroundImage: "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)", backgroundSize: "72px 72px" }} />
        <div style={{ ...pageWrap, position: "relative", zIndex: 1 }}>
          <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1.125fr", gap: isMobile ? 30 : 54, alignItems: "stretch" }}>
            <div>
              <ProductMark dark frameless mono scale={1.45} />
              <p style={{ fontSize: 15, lineHeight: "25px", color: "rgba(255,255,255,0.72)", maxWidth: 560, marginTop: 20 }}>
                OBSCURA trasforma documenti sensibili in contenuti sicuri, leggibili e pronti per l'analisi con strumenti AI, riducendo l'esposizione di dati personali e informazioni riservate.
              </p>
            </div>

            <div style={{ position: "relative", paddingLeft: isTablet ? 0 : 54 }}>
              {!isTablet && <span style={{ position: "absolute", left: 0, top: 0, bottom: -48, width: 1, background: "repeating-linear-gradient(to bottom, rgba(255,255,255,0.14) 0 5px, transparent 5px 10px)", WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, #000 12%, #000 88%, transparent 100%)", maskImage: "linear-gradient(to bottom, transparent 0%, #000 12%, #000 88%, transparent 100%)" }} />}
              {!isTablet && <span style={{ position: "absolute", left: -4, bottom: -52, width: 8, height: 8, background: "#fff", borderRadius: 1, boxShadow: "0 0 0 1px rgba(255,255,255,0.28)", zIndex: 2 }} />}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 24 : 54 }}>
                {[
                  { title: "Navigazione", links: [["Home", "#"], ["Chi siamo", "#chi-siamo"], ["Perché anonimizzare", "#perche"], ["Per chi è OBSCURA", "#per-chi"], ["Come funziona", "#come-funziona"], ["Prezzi", "#prezzi"], ["Contatti", "#contatti"]] },
                  { title: "Link utili", links: [["Demo anonimizzazione", "#per-chi"], ["Richiedi preventivo", "#contatti", () => setQuoteOpen(true)]] },
                  { title: "Extra", links: [["Privacy Policy", PRIVACY_POLICY_URL], ["Cookie policy", "?page=cookie"], ["Preferenze cookie", "#", () => apriPreferenzeCookie()]] },
                ].map((group) => (
                  <div key={group.title}>
                    <h3 style={{ fontSize: 12, fontWeight: 800, color: "rgba(255,255,255,0.54)", letterSpacing: "1.3px", textTransform: "uppercase", marginBottom: 16 }}>{group.title}</h3>
                    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                      {group.links.map(([label, href, onClick]) => {
                        const isPdf = href.endsWith(".pdf");
                        const linkStyle = { color: "rgba(255,255,255,0.84)", textDecoration: "none", fontSize: 14, fontWeight: 600 };
                        return (
                          <li key={label}>
                            {onClick ? (
                              <button
                                type="button"
                                onClick={onClick}
                                style={{ ...linkStyle, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
                              >
                                {label}
                              </button>
                            ) : (
                              <a
                                href={href}
                                {...(isPdf ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                                style={linkStyle}
                              >
                                {label}
                              </a>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginTop: isMobile ? 34 : 48, padding: isMobile ? "22px 0 0" : "28px 0 0", position: "relative", display: "grid", gridTemplateColumns: isTablet ? "1fr" : "1fr 1.125fr", gap: isMobile ? 24 : 54, alignItems: "start" }}>
            <span style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "repeating-linear-gradient(to right, rgba(255,255,255,0.14) 0 5px, transparent 5px 10px)", WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)", maskImage: "linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)" }} />
            <div>
              <p style={{ margin: "0 0 12px", fontSize: 11, color: "rgba(255,255,255,0.48)", fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase" }}>UN PRODOTTO DI</p>
              <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <img src={GRID_PLUS_LOGO_WHITE} alt="Logo GRID+" style={{ height: 48, maxWidth: 200, objectFit: "contain", opacity: 0.9 }} />
                <span style={{ width: 1, height: 22, background: "rgba(255,255,255,0.16)" }} />
                <img src={SAPIENZA_LOGO_WHITE} alt="Startup di Sapienza Università di Roma" style={{ height: 48, maxWidth: 200, objectFit: "contain", opacity: 0.9 }} />
              </div>
            </div>
            <div style={{ paddingLeft: isTablet ? 0 : 54, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 10 : 24 }}>
              <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
                <p style={{ margin: "0 0 12px", fontSize: 11, color: "rgba(255,255,255,0.48)", fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase" }}>INFORMAZIONI DI CONTATTO</p>
                <a href="mailto:info@gridplus.it" style={{ display: "block", color: C.blueAccent, textDecoration: "none", fontSize: 15, lineHeight: "25px", fontWeight: 400, marginBottom: 4 }}>info@gridplus.it</a>
                <a href="tel:+393387655663" style={{ display: "block", color: "rgba(255,255,255,0.72)", textDecoration: "none", fontSize: 15, lineHeight: "25px", fontWeight: 400, marginBottom: 4 }}>+39 3387655663</a>
                <p style={{ margin: 0, color: "rgba(255,255,255,0.72)", fontSize: 15, lineHeight: "25px", fontWeight: 400, whiteSpace: isMobile ? "normal" : "nowrap" }}>Via Andrea Ferrara, 45, Roma (RM), 00165</p>
              </div>
            </div>
          </div>
          <div style={{ marginTop: isMobile ? 22 : 28, display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: 10 }}>
            <p style={{ margin: 0, fontSize: 15, lineHeight: "25px", fontWeight: 400, color: "rgba(255,255,255,0.72)" }}>P. IVA 17387741006 · 10.000€ i.v. · RM - 1715269</p>
            <p style={{ margin: 0, fontSize: 15, lineHeight: "25px", fontWeight: 400, color: "rgba(255,255,255,0.72)", textAlign: isMobile ? "left" : "right" }}>
              GRID+ Copyright © {new Date().getFullYear()}. All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
      {quoteOpen && <QuoteModal isMobile={isMobile} onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}

/* ═══════════════════════════════════════════
   APP ROUTER
   ═══════════════════════════════════════════ */
export default function App() {
  // Pagina e parametri vengono dall'URL (vedi navigazione.js). Il cambio
  // pagina usa pushState, cosi' il tasto Indietro del browser torna alla
  // pagina precedente invece di uscire dal sito.
  const [page, setPage] = useState(() => paginaDaUrl(window.location.search));
  const [params, setParams] = useState(() => parametri(window.location.search));
  const navigateToPage = useCallback((nextPage, extra) => {
    const url = urlPerPagina(window.location.href, nextPage, extra);
    window.history.pushState({ page: nextPage }, document.title, url);
    setPage(nextPage);
    setParams(parametri(window.location.search));
    window.scrollTo(0, 0);
    traccia.paginaVista(nextPage);
  }, []);
  useEffect(() => {
    traccia.paginaVista(paginaDaUrl(window.location.search));
    const onPop = () => {
      const p = paginaDaUrl(window.location.search);
      setPage(p);
      setParams(parametri(window.location.search));
      window.scrollTo(0, 0);
      traccia.paginaVista(p);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return <>
    <style>{`*,*::before,*::after{box-sizing:border-box}html{scroll-behavior:smooth;scroll-padding-top:92px;overflow-x:clip;max-width:100%}[id]{scroll-margin-top:92px}body{margin:0;background:#fff;font-family:'Roboto',-apple-system,BlinkMacSystemFont,sans-serif;overflow-x:clip;max-width:100%}@keyframes obscura-flow{0%,100%{transform:translateX(-12%);opacity:.68}50%{transform:translateX(12%);opacity:1}}@keyframes obscura-grid-drift{from{background-position:0 0}to{background-position:64px 64px}}@keyframes ob-aurora-1{0%{transform:translate3d(-8%,-4%,0) scale(1)}50%{transform:translate3d(6%,4%,0) scale(1.1)}100%{transform:translate3d(-8%,-4%,0) scale(1)}}@keyframes ob-aurora-2{0%{transform:translate3d(6%,2%,0) scale(1.08)}50%{transform:translate3d(-8%,-6%,0) scale(1)}100%{transform:translate3d(6%,2%,0) scale(1.08)}}@keyframes ob-aurora-3{0%{transform:translate3d(0,6%,0) scale(1)}50%{transform:translate3d(-4%,-2%,0) scale(1.12)}100%{transform:translate3d(0,6%,0) scale(1)}}@keyframes ob-grid-drift{from{background-position:0 0}to{background-position:80px 80px}}@keyframes ob-rotate-slow{to{transform:rotate(360deg)}}@keyframes ob-marquee-rtl{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}@keyframes ob-marquee-ltr{0%{transform:translateX(-50%)}100%{transform:translateX(0)}}.obscura-marquee-track-rtl:hover,.obscura-marquee-track-ltr:hover{animation-play-state:paused}@media (prefers-reduced-motion:reduce){*,*::before,*::after{animation-duration:.01ms!important;animation-iteration-count:1!important;scroll-behavior:auto!important}.obscura-marquee-track-rtl,.obscura-marquee-track-ltr{animation:none!important}}`}</style>
    <AccountProvider>
      <CookieBanner onApriPolicy={() => navigateToPage("cookie")} />
      {page === "landing" ? <LandingPage onNavigate={() => navigateToPage("anonymize")} onNavigateTo={navigateToPage} />
        : page === "privacy" ? <PrivacyPolicyPage onBack={() => navigateToPage("landing")} />
        : page === "cookie" ? <CookiePolicyPage onBack={() => navigateToPage("landing")} />
        : page === "accesso" ? <AccessoPage onNavigate={navigateToPage} next={params.next} />
        : page === "registrazione" ? <RegistrazionePage onNavigate={navigateToPage} piano={params.piano} cadenza={params.cadenza} />
        : page === "verifica" ? <VerificaPage onNavigate={navigateToPage} token={params.token} tipo={params.tipo} />
        : page === "password" ? <PasswordPage onNavigate={navigateToPage} token={params.token} />
        : page === "invito" ? <InvitoPage onNavigate={navigateToPage} token={params.token} />
        : page === "account" ? <AccountPage onNavigate={navigateToPage} params={params} />
        : <AnonymizePage onNavigateTo={navigateToPage} />}
    </AccountProvider>
  </>;
}
